import React, {useEffect, useMemo, useRef, useState} from "react";
import {useParams, useLocation, useNavigate} from "react-router-dom";
import {supabase} from "./utils/supabaseClient";
import { getAppOrigin } from "./utils/url";
import {
    Alert,
    Box,
    Button,
    Center,
    Circle, Container, Divider,
    FormControl,
    FormLabel,
    Heading,
    HStack, Input, Radio, RadioGroup,
    Select, Spinner,
    Stack, Tab, TabList, TabPanel, TabPanels, Tabs,
    Text,
    AlertIcon, useToast, useRadio, useRadioGroup,
    Tbody, Tr, Td, Table, Thead, Th, Flex, Badge, SimpleGrid,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
    VStack, Grid, GridItem,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure
} from "@chakra-ui/react";
import AdditionalFields from "./AdditionalFields";
import {HiCheck, HiOutlineDocument, HiDocumentText, HiAnnotation, HiLightBulb, HiPaperAirplane, HiArrowLeft, HiCreditCard} from 'react-icons/hi'

import {CheckCircleIcon, Icon} from "@chakra-ui/icons";
import {motion} from "framer-motion";
import {CheckIcon} from "@chakra-ui/icons";
import PhoneInput, { isValidPhoneNumber as isValidPhone, parsePhoneNumber as parsePhone } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import countryList from 'react-select-country-list';
import {parsePhoneNumber, isValidPhoneNumber} from 'libphonenumber-js'
import moment from 'moment';
import { invoiceDueAmount } from "./utils/invoiceFields";
import NRIC from 'singapore-nric';
import { sendRegistrationEmail } from './utils/outlookEmailHelper';
import { getEffectivePolicy, getNextRegistrationStage } from './utils/enrollmentPolicy';
import { buildDirectoryParticipantPayload } from "./utils/directoryDraftPayload";
import {
    validateDirectoryRunListing,
    submitDirectoryParticipantDraft,
    submitDirectoryParticipantDraftsFullCertModules,
} from "./utils/directoryIntakeApi";
import {
    valuesEqualForConditionalRequirement,
    normalizeConditionalRequirements,
} from "./utils/conditionalFieldValues";
import RegistrationSearchableSelect from "./components/RegistrationSearchableSelect";
import { fetchPublicRegistration } from "./utils/registrationPortalApi";

// Function to get discount amount for a course
const getDiscountAmount = (course) => {
    if (course?.early_bird_discount && course?.early_bird_end_date) {
        const today = moment().format("YYYY-MM-DD");
        if (today <= course.early_bird_end_date) {
            return course.early_bird_discount;
        }
    }
    return 0;
};



const trackGAEvent = (category, action, label) => {
    if (window.gtag) {
        window.gtag('event', action, {
            event_category: category,
            event_label: label,
            send_to: 'G-RM3ERYY1NG'
        });
    } else {
        console.warn('Google Analytics not loaded');
    }
};

const trackFBEvent = (eventName, eventParams = {}, additionalParams) => {
    if (window.fbq) {
        // Check if the first parameter is 'track', 'trackCustom', or an event name
        if (eventName === 'track' || eventName === 'trackCustom') {
            // If using the method format (track/trackCustom, eventName, params)
            const method = eventName;
            const event = eventParams;
            const params = additionalParams || {};

            console.log(`FB Pixel: ${method}('${event}', `, params, ')');
            window.fbq(method, event, params);
        } else {
            // If using the direct format (eventName, params)
            console.log(`FB Pixel: track('${eventName}', `, eventParams, ')');
            window.fbq('track', eventName, eventParams);
        }
    } else {
        console.warn('Facebook Pixel not loaded', eventName, eventParams);
    }
};

const isDevelopment = process.env.NODE_ENV === 'development';

const devLog = (...args) => {
    if (isDevelopment) {
        console.log(...args);
    }
};

const devError = (...args) => {
    if (isDevelopment) {
        console.error(...args);
    }
};

/** Avoid duplicate edge invokes in one browser tab (double submit, dev StrictMode quirks). Server RPC also dedupes. */
function shouldSkipSessionEdgeInvoke(kind, registrationId) {
    try {
        if (typeof sessionStorage === 'undefined' || !registrationId) return false
        return sessionStorage.getItem(`lms_ef_${kind}_${registrationId}`) === '1'
    } catch (_) {
        return false
    }
}
function rememberSessionEdgeInvoke(kind, registrationId) {
    try {
        if (typeof sessionStorage === 'undefined' || !registrationId) return
        sessionStorage.setItem(`lms_ef_${kind}_${registrationId}`, '1')
    } catch (_) {}
}

function queueRegistrationReceivedEmail(registrationId) {
    if (shouldSkipSessionEdgeInvoke('registration_received', registrationId)) {
        console.warn('[Registration] Skipping duplicate send-registration-received-email invoke (same tab session)');
        return;
    }
    supabase.functions
        .invoke('send-registration-received-email', { body: { registrationId } })
        .then(({ error }) => {
            if (error) {
                console.error('❌ send-registration-received-email error:', error);
            } else {
                rememberSessionEdgeInvoke('registration_received', registrationId);
                console.log('✅ Registration received email queued');
            }
        })
        .catch((emailErr) => {
            console.error('❌ send-registration-received-email threw:', emailErr);
        });
}

const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

// Enhanced calendar component for module dates with duration
const ModuleCalendar = ({ course_dates, moduleCode }) => {
    if (!course_dates || course_dates.length === 0) return null;

    // Calculate duration for each session
    const sessionsWithDuration = course_dates.map(session => {
        const startTime = moment(session.start_time, "HHmm");
        const endTime = moment(session.end_time, "HHmm");
        const duration = endTime.diff(startTime, 'hours', true);
        
        return {
            ...session,
            duration: duration,
            durationText: duration >= 1 ? `${Math.floor(duration)}h ${Math.round((duration % 1) * 60)}m` : `${Math.round(duration * 60)}m`
        };
    });

    // Group dates by month
    const datesByMonth = sessionsWithDuration.reduce((acc, session) => {
        const date = new Date(session.date);
        const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
        const monthName = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        
        if (!acc[monthKey]) {
            acc[monthKey] = {
                monthName,
                dates: []
            };
        }
        
        acc[monthKey].dates.push({
            day: date.getDate(),
            startTime: session.start_time,
            endTime: session.end_time,
            duration: session.duration,
            durationText: session.durationText,
            dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'short' })
        });
        
        return acc;
    }, {});

    // Calculate total course duration
    const totalDuration = sessionsWithDuration.reduce((sum, session) => sum + session.duration, 0);
    const totalHours = Math.floor(totalDuration);
    const totalMinutes = Math.round((totalDuration % 1) * 60);

    return (
        <Box mt={2} p={2} bg="gray.50" borderRadius="md">
            <HStack justify="flex-end" mb={2}>
                <Badge colorScheme="green" fontSize="10px">
                    {totalHours}h {totalMinutes}m total
                </Badge>
            </HStack>
            
            <VStack spacing={2} align="stretch">
                {Object.values(datesByMonth).map((month, index) => (
                    <Box key={index}>
                        <Text fontSize="10px" fontWeight="600" color="gray.600" mb={1}>
                            {month.monthName}
                        </Text>
                        
                        {/* Table format for better readability */}
                        <Box overflowX="auto">
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <Th fontSize="10px" p={1}>Date</Th>
                                        <Th fontSize="10px" p={1}>Time</Th>
                                        <Th fontSize="10px" p={1}>Duration</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {month.dates.map((date, dateIndex) => (
                                        <Tr key={dateIndex}>
                                            <Td fontSize="10px" p={1}>
                                                <VStack spacing={0} align="start">
                                                    <Text fontWeight="600" fontSize="10px">{date.day}</Text>
                                                    <Text fontSize="9px" color="gray.500">
                                                        {date.dayOfWeek}
                                                    </Text>
                                                </VStack>
                                            </Td>
                                            <Td fontSize="10px" p={1}>
                                                <VStack spacing={0} align="start">
                                                    <Text fontWeight="600" fontSize="10px">
                                                        {moment(date.startTime, "HHmm").format("h:mm A")}
                                                    </Text>
                                                    <Text fontSize="9px" color="gray.500">
                                                        to {moment(date.endTime, "HHmm").format("h:mm A")}
                                                    </Text>
                                                </VStack>
                                            </Td>
                                            <Td fontSize="10px" p={1}>
                                                <Badge colorScheme="blue" fontSize="9px">
                                                    {date.durationText}
                                                </Badge>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>
                ))}
            </VStack>
        </Box>
    );
};

const validateNRIC = (nric, nationality) => {
    if (!nric) return false;
    
    // Only validate NRIC format for Singapore Citizens and PRs
    // For other nationalities, allow free text (passport/FIN numbers)
    const isSingaporeCitizenOrPR = nationality === "Singapore Citizen" || nationality === "Singapore Permanent Resident";
    
    if (!isSingaporeCitizenOrPR) {
        // For non-Singapore citizens/PRs, just check that something is entered
        return nric.trim().length > 0;
    }
    
    // For Singapore citizens/PRs, validate NRIC format
    const nricInstance = new NRIC(nric);
    return nricInstance.isValid && nricInstance.isCorrectFormat;
};

const validateUEN = (uen) => {
    if (!uen) return false;
    uen = uen.toUpperCase();

    // Business registered before 2009 (e.g., 123456789A)
    if (/^\d{9}[A-Z]$/.test(uen)) {
        return true;
    }

    // Local company registered before 2009 (e.g., 188000000A)
    if (/^\d{10}[A-Z]$/.test(uen)) {
        const yearPrefix = parseInt(uen.substring(0, 4));
        return yearPrefix >= 1800 && yearPrefix <= 2025;
    }

    // Other entities after 2009 (e.g., T09LL0001A)
    if (/^[TSR]\d{2}[A-Z]{2}\d{4}[A-Z]$/.test(uen)) {
        const year = parseInt(uen.substring(1, 3));
        return year >= 9 && year <= 25;
    }

    return false;
};

// Floating Progress Bar — shows compulsory field completion at the top of the form
const FloatingProgressBar = ({
    filledCount,
    totalCount,
    emailVerified,
    emailVerificationSent,
    participantEmail,
    onResendVerification,
    isSendingVerification,
    showEmailVerification
}) => {
    const progressPercent = totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;
    const isComplete = filledCount === totalCount;

    return (
        <Box
            position="sticky"
            top={0}
            left={0}
            right={0}
            zIndex={100}
            bg="white"
            borderBottom="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            transition="all 0.3s ease"
        >
            <Box px={{base: 4, md: 6}} py={{base: 2, md: 3}}>
                <Flex align="center" justify="space-between" mb={1}>
                    <HStack spacing={2}>
                        <Text fontSize={{base: "xs", md: "sm"}} fontWeight="600" color="gray.700">
                            {isComplete ? "✓ All fields complete" : "Form Progress"}
                        </Text>
                    </HStack>
                    <Badge
                        colorScheme={isComplete ? "green" : progressPercent > 50 ? "blue" : "orange"}
                        fontSize={{base: "10px", md: "xs"}}
                        px={2}
                    >
                        {filledCount}/{totalCount}
                    </Badge>
                </Flex>
                <Box
                    w="100%"
                    h={{base: "4px", md: "6px"}}
                    bg="gray.100"
                    borderRadius="full"
                    overflow="hidden"
                >
                    <Box
                        h="100%"
                        w={`${progressPercent}%`}
                        bgGradient={isComplete
                            ? "linear(to-r, green.400, green.500)"
                            : "linear(to-r, brand.400, brand.600)"
                        }
                        borderRadius="full"
                        transition="width 0.4s ease-out"
                    />
                </Box>
            </Box>

            {showEmailVerification && participantEmail && !emailVerified && (
                <Box
                    px={{base: 4, md: 6}}
                    py={{base: 2, md: 2.5}}
                    bg="orange.50"
                    borderTop="1px solid"
                    borderColor="orange.100"
                >
                    <Flex
                        align="center"
                        justify="space-between"
                        gap={2}
                        flexWrap={{base: "wrap", md: "nowrap"}}
                    >
                        <HStack spacing={2} flex="1" minW={0}>
                            <Text fontSize="md">📧</Text>
                            <Text
                                fontSize={{base: "xs", md: "sm"}}
                                color="orange.800"
                                fontWeight="500"
                                isTruncated
                            >
                                Verify: <Text as="span" fontWeight="600">{participantEmail}</Text>
                            </Text>
                        </HStack>
                        <Button
                            size="xs"
                            colorScheme="orange"
                            variant="solid"
                            onClick={onResendVerification}
                            isLoading={isSendingVerification}
                            loadingText="..."
                            flexShrink={0}
                        >
                            Resend
                        </Button>
                    </Flex>
                </Box>
            )}

            {showEmailVerification && emailVerified && (
                <Box
                    px={{base: 4, md: 6}}
                    py={2}
                    bg="green.50"
                    borderTop="1px solid"
                    borderColor="green.100"
                >
                    <HStack spacing={2}>
                        <Text fontSize="md">✅</Text>
                        <Text fontSize={{base: "xs", md: "sm"}} color="green.700" fontWeight="500">
                            Email verified
                        </Text>
                    </HStack>
                </Box>
            )}
        </Box>
    );
};

/** Splits "Primary//Secondary" labels into two lines: primary bold, secondary italic + smaller below. */
function BilingualLabel({ label, isRequired, requiredColor = "red.500" }) {
    const delim = '//';
    const slashIdx = label ? label.indexOf(delim) : -1;
    const primary = slashIdx === -1 ? label : label.slice(0, slashIdx).trim();
    const secondary = slashIdx === -1 ? null : label.slice(slashIdx + delim.length).trim();
    return (
        <>
            {primary}
            {isRequired && <Text as="span" color={requiredColor} ml={1}>*</Text>}
            {secondary && (
                <Text as="div" fontSize="xs" fontWeight="normal" fontStyle="italic" color="gray.500" mt={0.5} lineHeight="tight">
                    {secondary}
                </Text>
            )}
        </>
    );
}

const Step1 = (props) => {
    const {
        courseRuns,
        formData,
        setFormData,
        allowedTab,
        setTabIndex,
        validationErrors,
        setValidationErrors,
        isTeamRegistration,
        setIsTeamRegistration,
        isSME,
        setIsSME,
        isUENValid,
        setIsUENValid,
        calculatePrice,
        registrationId,
        setRegistrationId,
        selectedCourseRunIds,
        setSelectedCourseRunIds,
        fullCertificationCost,
        additionalFields,
        templateFieldIds,
        orgGstSettings,
        selectedFiles,
        setSelectedFiles,
        uploadingFiles,
        setUploadingFiles,
        existingRegistration,
        setEmailVerified,
        basicFieldsOrder = [],
    } = props
    // Returns the DB config row for a system field, or undefined (→ falls back to legacy behaviour).
    const getOriginalFieldConfig = (fieldKey) => basicFieldsOrder.find(f => f.field_key === fieldKey);
    // Returns true when the field should be shown given the active template.
    // null templateFieldIds = no template set → show everything.
    // If the field has no DB config row yet (legacy), always show it.
    const isSystemFieldInTemplate = (fieldKey) => {
        if (templateFieldIds === null) return true;
        const config = getOriginalFieldConfig(fieldKey);
        if (!config) return true;
        return templateFieldIds.includes(config.id);
    };
    // ISO 3166-1 country list — used when nationality field_type = 'country'
    const countryOptions = useMemo(() => countryList().getData(), []);
    const [courseError, setCourseError] = useState(null);
    const [selectedCourseRunId, setSelectedCourseRunId] = useState('');
    // In amendment mode the email field is locked until the user explicitly unlocks it
    const [emailEditable, setEmailEditable] = useState(!existingRegistration);
    const [formSequence, setFormSequence] = useState({
        courseSelected: false,
        nationalityFilled: false,
        ageFilled: false,
        nameFilled: false,
        emailFilled: false
    });

    // Add these hooks to get location and navigate
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();

    // Update form state based on URL params when the component mounts
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const courseRunId = params.get('course_run_id');
        const certificate = params.get('certificate');
        const fullCertification = params.get('full_certification');
        const course = params.get('course');

        if (courseRunId && courseRuns.length > 0) {  // Only proceed if we have course data
            // courseRunId is now the course_run.id (text code), not attendance_id (UUID)
            const selectedCourse = courseRuns.find(course => course.id === courseRunId);
            if (selectedCourse) {
                setSelectedCourseRunId(courseRunId); // Set the radio selection
                setFormData((prevData) => ({
                    ...prevData,
                    registration_type: params.get('registration_type') || '',
                    course_run_id: courseRunId, // This is now the id (text code)
                    course_type_code: selectedCourse.course_type_code,
                    id: selectedCourse.id || '',
                }));
            } else {
                toast({
                    title: "Course not found",
                    description: "The selected course is not available",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        }

        // If full certification parameter is provided, set the certification ID
        if (fullCertification) {
            setFormData((prevData) => ({
                ...prevData,
                full_certification_id: fullCertification,
            }));
        }

        // If single course parameter is provided, set the course code
        if (course) {
            setFormData((prevData) => ({
                ...prevData,
                course_type_code: course,
            }));
        }

        // If certificate parameter is provided, pre-select the course type
        if (certificate && courseRuns.length > 0) {
            // Find a course that has the matching certification
            const certificateCourse = courseRuns.find(course => course.course_type?.certification === certificate);
            if (certificateCourse) {
                setFormData((prevData) => ({
                    ...prevData,
                    course_type_code: certificateCourse.course_type_code,
                }));
            }
        }
    }, [location.search, courseRuns]); // Dependencies include courseRuns to re-run when data is loaded

    // Update sequence state when course is selected
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const fullCertification = params.get('full_certification');
        
        if (fullCertification) {
            // For full certifications, check if any modules are selected
            setFormSequence(prev => ({
                ...prev,
                courseSelected: selectedCourseRunIds.length > 0
            }));
        } else {
            // For regular courses, check single course selection
            setFormSequence(prev => ({
                ...prev,
                courseSelected: !!formData.course_run_id
            }));
        }
    }, [formData.course_run_id, selectedCourseRunIds, location.search]);

    // Auto-select all modules when certification run is selected
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const fullCertification = params.get('full_certification');
        
        console.log('Auto-selection check:', {
            fullCertification,
            courseTypeCode: formData.course_type_code,
            courseRunsLength: courseRuns.length
        });
        
        if (fullCertification && formData.course_type_code && courseRuns.length > 0) {
            // Find all courses for this certification run
            const certificationCourses = courseRuns.filter(course => 
                course.full_certification_id === fullCertification &&
                course.certification_run_id === formData.course_type_code &&
                course.is_full_certification === true
            );
            
            console.log('Found certification courses:', certificationCourses.length, certificationCourses);
            
            if (certificationCourses.length > 0) {
                const allCourseRunIds = certificationCourses.map(course => course.id);
                setSelectedCourseRunIds(allCourseRunIds);
                console.log('Auto-selected course run IDs:', allCourseRunIds);
            } else {
                console.log('No certification courses found. Available courses:', courseRuns.filter(c => c.full_certification_id === fullCertification));
            }
        }
    }, [formData.course_type_code, courseRuns, location.search]);

    // Preserve selectedCourseRunIds when form data changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const fullCertification = params.get('full_certification');
        
        if (fullCertification && selectedCourseRunIds.length === 0 && courseRuns.length > 0) {
            // Re-select modules if they were cleared
            const certificationCourses = courseRuns.filter(course => 
                course.full_certification_id === fullCertification &&
                course.certification_run_id === formData.course_type_code &&
                course.is_full_certification === true
            );
            
            if (certificationCourses.length > 0) {
                const allCourseRunIds = certificationCourses.map(course => course.id);
                setSelectedCourseRunIds(allCourseRunIds);
                console.log('Re-selected course run IDs after form change:', allCourseRunIds);
            }
        }
    }, [formData, courseRuns, location.search, selectedCourseRunIds.length]);

    // Reset registration_type when the selected course changes and is not an SSG course
    useEffect(() => {
        if (!formData.course_run_id || courseRuns.length === 0) return;
        const selectedCourse = courseRuns.find(c => c.id === formData.course_run_id);
        if (selectedCourse && !selectedCourse.ssg_course) {
            setFormData(prev => ({
                ...prev,
                registration_type: '',
                sponsor_uen: '',
            }));
        }
    }, [formData.course_run_id, courseRuns]);

    const handleChange = (name, value) => {
        if (name === "course_type_code") {
            // Check if this is a certification run selection (for full certifications)
            const params = new URLSearchParams(location.search);
            const fullCertification = params.get('full_certification');
            
            if (fullCertification) {
                // For full certifications, course_type_code stores the certification_run_id
                setFormData((prevData) => ({
                    ...prevData,
                    [name]: value,
                    course_run_id: '',
                    id: '',
                    certification_run_id: value
                }));
            } else {
                // Regular course type selection
                setFormData((prevData) => ({
                    ...prevData,
                    [name]: value,
                    course_run_id: '',
                    id: '',
                    diploma_group: '' // Reset diploma group when changing course type
                }));
            }
            setSelectedCourseRunId('');
        } else if (name === "diploma_group") {
            // When diploma group is selected, set it and reset course run
            setFormData((prevData) => ({
                ...prevData,
                [name]: value,
                course_run_id: '',
                id: ''
            }));
            setSelectedCourseRunId('');
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: value,
            }));

            // Update sequence state based on field
            switch (name) {
                case "nationality":
                    setFormSequence(prev => ({
                        ...prev,
                        nationalityFilled: !!value
                    }));
                    break;
                case "birth_age":
                    setFormSequence(prev => ({
                        ...prev,
                        ageFilled: !!value
                    }));
                    break;
                case "participant_name":
                    setFormSequence(prev => ({
                        ...prev,
                        nameFilled: !!value
                    }));
                    break;
                case "participant_email":
                    setFormSequence(prev => ({
                        ...prev,
                        emailFilled: !!value
                    }));
                    break;
                default:
                    break;
            }
        }

        if (name === "course_run_id" || name === "registration_type") {
            const params = new URLSearchParams(location.search);
            params.set(name, value);
            navigate({search: params.toString()}, {replace: true});
        }
    };

    const {getRootProps, getRadioProps} = useRadioGroup({
        name: 'course_run_id',
        value: selectedCourseRunId,
        onChange: (value) => {
            setSelectedCourseRunId(value);
            // value is now the course_run.id (text code), not attendance_id (UUID)
            const selectedCourse = courseRuns.find(
                (course) => course.id === value);
            if (selectedCourse) {
                setFormData((prevData) => ({
                    ...prevData,
                    course_run_id: value, // This is now the id (text code), not attendance_id
                    course_type_code: selectedCourse.course_type_code,
                    id: selectedCourse.id
                }));

                // Update URL with the course_run.id (text code)
                const params = new URLSearchParams(location.search);
                params.set('course_run_id', value);
                navigate({search: params.toString()}, {replace: true});
            }

            // Track course selection
            trackFBEvent('trackCustom', 'FormStep_CourseSelected', {
                step_number: 1,
                course_id: value
            });
        }
    });

    const group = getRootProps();

    // const calculatePrice = (course) => {
    //     // Base price calculation
    //     const basePrice = course?.name === "Python and GPT4 (ChatGPT) For Beginners" ? 1050 : course?.price;
    //
    //     // If not an SSG course, return base price without any subsidy
    //     if (!course?.ssg_course) {
    //         return basePrice;
    //     }
    //
    //     // Company-Sponsored cases
    //     if (formData.registration_type === "Company-Sponsored") {
    //         // SME case
    //         if (isSME === "yes" &&
    //             (formData.nationality === "Singapore Citizen" || formData.nationality === "Singapore Permanent Resident")) {
    //             return 0.3 * basePrice; // 70% subsidy
    //         }
    //
    //         // Non-SME case - apply same logic as self-sponsored
    //         if (isSME === "no") {
    //             if (formData.nationality === "Singapore Citizen") {
    //                 return parseInt(formData.birth_age) >= 40 ? 0.3 * basePrice : 0.5 * basePrice;
    //             }
    //             if (formData.nationality === "Singapore Permanent Resident") {
    //                 return 0.5 * basePrice;
    //             }
    //         }
    //     }
    //
    //     // Self-Sponsored cases
    //     if (formData.registration_type === "Self-Sponsored") {
    //         if (!formData.birth_age || !formData.nationality) {
    //             return basePrice;
    //         }
    //
    //         if (formData.nationality === "Singapore Citizen") {
    //             return parseInt(formData.birth_age) >= 40 ? 0.3 * basePrice : 0.5 * basePrice;
    //         }
    //         if (formData.nationality === "Singapore Permanent Resident") {
    //             return 0.5 * basePrice;
    //         }
    //     }
    //
    //     return basePrice;
    // };

    useEffect(() => {
        // Use a ref to track if the event was already fired
        if (!window._pixelStartRegistrationFired) {
            trackGAEvent('Registration', 'form_started', 'initial_load');
            trackFBEvent('trackCustom', 'StartRegistration', {});
            // Set flag to prevent duplicate fires
            window._pixelStartRegistrationFired = true;
        }
    }, []);

    // Track when user selects a course
    const handleCourseSelection = (value) => {
        trackGAEvent('Registration', 'course_selected', value);
        // ... existing course selection logic
    };

    useEffect(() => {
        const handleBeforeUnload = () => {
            // Track form abandonment with Google Analytics
            if (!formData.course_run_id) {
                trackGAEvent('Registration', 'form_abandoned', 'before_course_selection');
                trackFBEvent('trackCustom', 'FormAbandoned', {
                    stage: 'before_course_selection'
                });
            } else if (!formData.participant_email) {
                trackGAEvent('Registration', 'form_abandoned', 'before_email_entry');
                trackFBEvent('trackCustom', 'FormAbandoned', {
                    stage: 'before_email_entry'
                });
            } else if (!formData.mobile_no) {
                trackGAEvent('Registration', 'form_abandoned', 'before_phone_entry');
                trackFBEvent('trackCustom', 'FormAbandoned', {
                    stage: 'before_phone_entry'
                });
            } else {
                // Tracks abandonment after all required fields are filled
                trackFBEvent('trackCustom', 'FormAbandoned', {
                    stage: 'form_nearly_complete'
                });
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [formData]);

    return (
        <Box w={"100%"}
             borderRadius="md"
             p={6}
             pb={formData.course_run_id ? 32 : 6}
        >
            {/* Read-only course info shown when amending a rejected registration */}
            {existingRegistration && (() => {
                const selectedRun = courseRuns.find(c => c.id === formData.course_run_id);
                return (
                    <Box mb={5} p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.300">
                        <Text fontWeight="bold" fontSize="sm" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="wide">
                            Course Selection
                        </Text>
                        <HStack spacing={2} align="center" flexWrap="wrap">
                            <Text fontWeight="semibold" fontSize="md" color="gray.800">
                                {selectedRun?.name || formData.course_run_id}
                            </Text>
                            <Badge colorScheme="gray" variant="subtle" fontSize="xs">{formData.course_run_id}</Badge>
                        </HStack>
                        <Badge colorScheme="blue" variant="subtle" mt={2} fontSize="xs">{formData.course_type_code}</Badge>
                        {selectedRun && (selectedRun.start_date || selectedRun.end_date) && (
                            <Text fontSize="sm" color="gray.600" mt={2}>
                                {selectedRun.start_date ? moment(selectedRun.start_date).format('DD MMM YYYY') : ''}
                                {selectedRun.start_date && selectedRun.end_date ? ' – ' : ''}
                                {selectedRun.end_date ? moment(selectedRun.end_date).format('DD MMM YYYY') : ''}
                            </Text>
                        )}
                    </Box>
                );
            })()}
            <FormControl display={existingRegistration ? 'none' : 'block'}>
                {(() => {
                    const params = new URLSearchParams(location.search);
                    const fullCertification = params.get('full_certification');
                    
                    if (fullCertification && courseRuns.length > 0) {
                        const certInfo = courseRuns.find(course => course.full_certification_id === fullCertification);
                        if (certInfo) {
                            return (
                                <Box mb={4} p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                                    <Text fontSize="sm" color="blue.600" fontWeight="bold" mb={1}>
                                        Selected Program
                                    </Text>
                                    <Text fontSize="lg" color="blue.800" fontWeight="bold">
                                        {certInfo.full_certification_name}
                                    </Text>
                                </Box>
                            );
                        }
                    }
                    return null;
                })()}
                <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">
                    {(() => {
                        const params = new URLSearchParams(location.search);
                        const certificate = params.get('certificate');
                        const fullCertification = params.get('full_certification');
                        
                        if (fullCertification || certificate === 'Diploma in Caregiving') {
                            return 'Certification Run';
                        } else {
                            return 'Course Type';
                        }
                    })()}
                </FormLabel>
                <Select
                    name="course_type"
                    value={formData.course_type_code}
                    placeholder='Select course type'
                    bg={"white"}
                    onChange={(e) => handleChange("course_type_code", e.target.value)}
                >
                    {(() => {
                        const params = new URLSearchParams(location.search);
                        const certificate = params.get('certificate');
                        const fullCertification = params.get('full_certification');
                        
                        // If full certification is selected, show certification runs
                        if (fullCertification) {
                            const certificationRuns = [...new Map(
                                courseRuns
                                    .filter(course => course.full_certification_id === fullCertification && course.certification_run_id)
                                    .map(course => [
                                        course.certification_run_id,
                                        {
                                            id: course.certification_run_id,
                                            name: course.certification_run_name,
                                            start_date: course.certification_run_start,
                                            end_date: course.certification_run_end,
                                            certification_name: course.full_certification_name
                                        }
                                    ])
                            ).values()].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                            
                            return certificationRuns.map((run) => (
                                <option key={run.id} value={run.id}>
                                    {run.name} ({moment(run.start_date).format('MMM YYYY')} - {moment(run.end_date).format('MMM YYYY')})
                                </option>
                            ));
                        }
                        // If certificate is "Diploma in Caregiving", show diploma groups instead of individual course types
                        else if (certificate === 'Diploma in Caregiving') {
                            const diplomaGroups = [...new Map(
                                courseRuns
                                    .filter(course => course.certification === 'Diploma in Caregiving' && course.diploma_group)
                                    .map(course => [
                                        course.diploma_group,
                                        {
                                            code: course.diploma_group,
                                            name: course.diploma_group,
                                            start_date: course.diploma_start_date,
                                            end_date: course.diploma_end_date,
                                            ssg_course: course.ssg_course,
                                            certification: course.certification
                                        }
                                    ])
                            ).values()].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                            
                            return diplomaGroups.map((group) => (
                                <option key={group.code} value={group.code}>
                                    {group.name} ({moment(group.start_date).format('MMM YYYY')} - {moment(group.end_date).format('MMM YYYY')})
                                </option>
                            ));
                        } else {
                            // Only show individual courses (NOT full certifications)
                            return [...new Map(courseRuns
                                .filter(course => 
                                    course.is_full_certification === false &&
                                    (course.certification === null || 
                                     course.certification === 'others' ||
                                     course.certification === undefined)
                                )
                                .map(course => [
                                    course.course_type_code,
                                    {
                                        code: course.course_type_code,
                                        name: course.name ? course.name.split(' - ')[0] : course.course_type?.name || 'Unknown Course',
                                        ssg_course: course.ssg_course,
                                        certification: course.course_type?.certification
                                    }
                                ])
                            ).values()].map((type) => {
                                return (
                                    <option key={type.code} value={type.code}>
                                        {type.name}{type.ssg_course ? " (SSG Subsidy Available!)" : ""}
                                    </option>
                                );
                            });
                        }
                    })()}
                </Select>
            </FormControl>


            <FormControl mt={5} display={existingRegistration ? 'none' : 'block'}>
                <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">
                    {(() => {
                        const params = new URLSearchParams(location.search);
                        const certificate = params.get('certificate');
                        const fullCertification = params.get('full_certification');
                        
                        if (fullCertification && formData.course_type_code) {
                            return 'Certification Modules';
                        } else if (certificate === 'Diploma in Caregiving' && formData.course_type_code) {
                            return 'Diploma Modules';
                        } else if (['D1', 'D2', 'D3', 'D4', 'D5', 'D6'].includes(formData.course_type_code) && formData.diploma_group) {
                            return 'Diploma Modules';
                        } else {
                            return 'Available Course Runs';
                        }
                    })()}
                </FormLabel>
                {formData.course_type_code ? (
                    <Box width="100%">
                        {(() => {
                            const params = new URLSearchParams(location.search);
                            const certificate = params.get('certificate');
                            const fullCertification = params.get('full_certification');
                            
                            // If coming from full certification selection, use accordion layout
                            if (fullCertification && formData.course_type_code) {
                                console.log('Filtering for full certification:', {
                                    fullCertification,
                                    courseTypeCode: formData.course_type_code,
                                    totalCourses: courseRuns.length
                                });
                                
                                const filteredCourses = courseRuns.filter(course => 
                                    course.full_certification_id === fullCertification &&
                                    course.certification_run_id === formData.course_type_code &&
                                    course.is_full_certification === true
                                );
                                
                                console.log('After initial filtering:', filteredCourses.length, filteredCourses);
                                
                                // Temporarily disable date filtering to debug
                                const dateFilteredCourses = filteredCourses;
                                
                                // const dateFilteredCourses = filteredCourses.filter(course => {
                                //     const today = new Date();
                                //     const registrationEndDate = new Date(course.registration_closing_date);
                                //     const registrationStartDate = new Date(course.registration_opening_date);
                                    
                                //     const isEndDateValid = registrationEndDate >= today;
                                //     const isStartDateValid = registrationStartDate <= today;
                                    
                                //     console.log('Date filtering for course:', course.course_type_code, {
                                //         today: today.toISOString(),
                                //         registrationEndDate: registrationEndDate.toISOString(),
                                //         registrationStartDate: registrationStartDate.toISOString(),
                                //         isEndDateValid,
                                //         isStartDateValid
                                //     });
                                    
                                //     return isEndDateValid && isStartDateValid;
                                // });
                                
                                console.log('After date filtering:', dateFilteredCourses.length, dateFilteredCourses);
                                
                                const sortedCourses = dateFilteredCourses.sort((a, b) => {
                                    // Sort by sequence order from certification_modules
                                    return (a.sequence_order || 0) - (b.sequence_order || 0);
                                });
                                
                                console.log('Final sorted courses:', sortedCourses.length, sortedCourses);

                                // Group modules by module_tag dynamically
                                const phases = {};
                                sortedCourses.forEach(course => {
                                    const tag = course.module_tag || 'Other';
                                    const phaseName = tag.charAt(0).toUpperCase() + tag.slice(1);
                                    
                                    if (!phases[phaseName]) {
                                        phases[phaseName] = [];
                                    }
                                    phases[phaseName].push(course);
                                });
                                
                                console.log('Dynamic phases:', phases);

                                return (
                                    <Box>
                                        <Box mb={4} p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                                            <Text fontSize="sm" color="blue.600" mb={2} fontWeight="bold">
                                                Full Certification Program
                                            </Text>
                                            <Text fontSize="xs" color="blue.500">
                                                You will be automatically registered for all {sortedCourses.length} modules in this certification program.
                                            </Text>
                                        </Box>
                                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                            {sortedCourses.map((course, index) => (
                                                <DiplomaModuleCard
                                                    key={course.id}
                                                    course_name={course.name}
                                                    start_date={course.start_date}
                                                    end_date={course.end_date}
                                                    course_dates={course.course_dates}
                                                    early_bird_discount={course.early_bird_discount}
                                                    early_bird_end_date={course.early_bird_end_date}
                                                    is_course_run_confirmed={course.is_course_run_confirmed}
                                                    course_type_code={course.course_type_code}
                                                    module_tag={course.module_tag}
                                                    is_diploma_module={true}
                                                    value={course.id}
                                                    isChecked={true}
                                                    isDisabled={true}
                                                    onChange={() => {}} // Disabled - no selection needed
                                                />
                                            ))}
                                        </SimpleGrid>
                                        
                                    </Box>
                                );
                            }
                            // If coming from certificate selection with "Diploma in Caregiving", use accordion layout
                            else if (certificate === 'Diploma in Caregiving' && formData.course_type_code) {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const filteredCourses = courseRuns.filter(course => 
                                    course.certification === 'Diploma in Caregiving' &&
                                    course.diploma_group === formData.course_type_code
                                ).filter(course => {
                                    if (!course.start_date) return true;
                                    const startDate = new Date(course.start_date);
                                    startDate.setHours(0, 0, 0, 0);
                                    return startDate >= today;
                                }).sort((a, b) => {
                                    const codeOrder = ['D1', 'D2', 'D3', 'D4', 'D5', 'D6'];
                                    const aIndex = codeOrder.indexOf(a.course_type_code);
                                    const bIndex = codeOrder.indexOf(b.course_type_code);
                                    return aIndex - bIndex;
                                });

                                // Group modules by phases
                                const phases = {
                                    "Phase 1: Foundation": filteredCourses.filter(course => ['D1', 'D2', 'D3'].includes(course.course_type_code)),
                                    "Phase 2: Specialization": filteredCourses.filter(course => ['D4', 'D5'].includes(course.course_type_code)),
                                    "Phase 3: Capstone": filteredCourses.filter(course => ['D6'].includes(course.course_type_code))
                                };

                                return (
                                    <Accordion allowMultiple>
                                        {Object.entries(phases).map(([phaseName, modules]) => {
                                            if (modules.length === 0) return null;
                                            
                                            return (
                                                <AccordionItem key={phaseName} border="1px solid" borderColor="brand.200" borderRadius="md" mb={2}>
                                                    <h2>
                                                        <AccordionButton>
                                                            <Box flex="1" textAlign="left">
                                                                <HStack>
                                                                    <Text fontWeight="bold" color="brand.700">
                                                                        {phaseName}
                                                                    </Text>
                                                                    <Badge colorScheme="blue" fontSize="xs">
                                                                        {modules.length} module{modules.length > 1 ? 's' : ''}
                                                                    </Badge>
                                                                </HStack>
                                                            </Box>
                                                            <AccordionIcon />
                                                        </AccordionButton>
                                                    </h2>
                                                    <AccordionPanel pb={4}>
                                                        <SimpleGrid
                                                            columns={{base: 1, md: 2, lg: 3}}
                                                            spacing={4}
                                                            {...group}
                                                        >
                                                            {modules.map((option) => {
                                                                const radio = getRadioProps({value: option.id});
                                                                return (
                                                                    <DiplomaModuleCard
                                                                        key={option.id}
                                                                        {...radio}
                                                                        course_name={option.name}
                                                                        start_date={option.start_date}
                                                                        end_date={option.end_date}
                                                                        course_dates={option.course_dates}
                                                                        early_bird_discount={option.early_bird_discount}
                                                                        early_bird_end_date={option.early_bird_end_date}
                                                                        course_type_code={option.course_type_code}
                                                                        is_diploma_module={true}
                                                                    />
                                                                );
                                                            })}
                                                        </SimpleGrid>
                                                    </AccordionPanel>
                                                </AccordionItem>
                                            );
                                        })}
                                    </Accordion>
                                );
                            } else {
                                // Regular course display logic (unchanged)
                                let filteredCourses = courseRuns;
                                
                                if (['D1', 'D2', 'D3', 'D4', 'D5', 'D6'].includes(formData.course_type_code) && formData.diploma_group) {
                                    filteredCourses = courseRuns.filter(course => 
                                        ['D1', 'D2', 'D3', 'D4', 'D5', 'D6'].includes(course.course_type_code) &&
                                        (course.diploma_group === formData.diploma_group || course.certification === formData.diploma_group)
                                    );
                                } else {
                                    filteredCourses = courseRuns.filter(course => course.course_type_code === formData.course_type_code);
                                }
                                
                                // Show runs as long as start_date is today or in the future (no registration window filter)
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const availableCourses = filteredCourses.filter(course => {
                                    if (!course.start_date) return true;
                                    const startDate = new Date(course.start_date);
                                    startDate.setHours(0, 0, 0, 0);
                                    return startDate >= today;
                                });
                                
                                // Show sorry message if no course runs are available
                                if (availableCourses.length === 0) {
                                    return (
                                        <Box textAlign="center" py={8}>
                                            <Text fontSize="lg" color="gray.600" mb={4}>
                                                Sorry, there are currently no available course runs for this course.
                                            </Text>
                                            <Text fontSize="sm" color="gray.500">
                                                Please check back later or contact us for more information.
                                            </Text>
                                        </Box>
                                    );
                                }
                                
                                return (
                                    <SimpleGrid
                                        columns={{base: 1, md: 2, lg: 3}}
                                        spacing={4}
                                        {...group}
                                    >
                                        {filteredCourses
                                            .sort((a, b) => {
                                                const codeOrder = ['D1', 'D2', 'D3', 'D4', 'D5', 'D6'];
                                                const aIndex = codeOrder.indexOf(a.course_type_code);
                                                const bIndex = codeOrder.indexOf(b.course_type_code);
                                                
                                                if (aIndex !== -1 && bIndex !== -1) {
                                                    return aIndex - bIndex;
                                                }
                                                return new Date(a.start_date) - new Date(b.start_date);
                                            })
                                            .map((option) => {
                                                // Use course_run.id (text code) instead of attendance_id (UUID)
                                                const radio = getRadioProps({value: option.id});
                                                return (
                                                    <RadioCard
                                                        key={option.id}
                                                        {...radio}
                                                        course_name={option.name}
                                                        start_date={option.start_date}
                                                        end_date={option.end_date}
                                                        course_dates={option.course_dates}
                                                        early_bird_discount={option.early_bird_discount}
                                                        early_bird_end_date={option.early_bird_end_date}
                                                        course_type_code={option.course_type_code}
                                                        is_diploma_module={['D1', 'D2', 'D3', 'D4', 'D5', 'D6'].includes(option.course_type_code)}
                                                    />
                                                );
                                            })}
                                    </SimpleGrid>
                                );
                            }
                        })()}
                    </Box>
                ) : (
                    <Text color="brand.500" fontSize={{base: "xs", md: "sm"}}>Please select a course type to
                        view available course runs.</Text>
                )}
            </FormControl>

            {/* Only show form fields after user has made a selection */}
            {(() => {
                const params = new URLSearchParams(location.search);
                const fullCertification = params.get('full_certification');
                
                // For full certifications: show form only if certification run is selected
                if (fullCertification) {
                    return formData.course_type_code && formData.course_type_code !== '';
                }
                
                // For individual courses: show form only if a course run is selected
                return formData.course_run_id && formData.course_run_id !== '';
            })() && (
                <>
            {/* Only show Registration Type for SSG-claimable courses */}
            {(() => {
                const params = new URLSearchParams(location.search);
                const fullCertification = params.get('full_certification');
                const isSSGCourse = fullCertification
                    ? courseRuns.some(c => c.full_certification_id === fullCertification && c.ssg_course)
                    : courseRuns.find(c => c.id === formData.course_run_id)?.ssg_course === true;
                if (!isSSGCourse) return null;
                return (
            <>
            <FormControl mt={5}>
                <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">Registration Type</FormLabel>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                    <Button
                        onClick={() => {
                            handleChange("registration_type", "Self-Sponsored");
                            trackGAEvent('Registration', 'type_selected', 'Self-Sponsored');
                            trackFBEvent('trackCustom', 'FormStep_RegistrationType', {
                                step_number: 2,
                                registration_type: "Self-Sponsored"
                            });
                        }}
                        colorScheme={formData.registration_type === "Self-Sponsored" ? "blue" : "gray"}
                        variant={formData.registration_type === "Self-Sponsored" ? "solid" : "outline"}
                        size="md"
                        py={{ base: 4, md: 5 }}
                        px={4}
                        height="auto"
                        whiteSpace="normal"
                        borderColor={formData.registration_type === "Self-Sponsored" ? "blue.500" : "gray.300"}
                    >
                        Self-Sponsored
                    </Button>
                    <Button
                        onClick={() => {
                            handleChange("registration_type", "Company-Sponsored");
                            trackGAEvent('Registration', 'type_selected', 'Company-Sponsored');
                            trackFBEvent('trackCustom', 'FormStep_RegistrationType', {
                                step_number: 2,
                                registration_type: "Company-Sponsored"
                            });
                        }}
                        colorScheme={formData.registration_type === "Company-Sponsored" ? "blue" : "gray"}
                        variant={formData.registration_type === "Company-Sponsored" ? "solid" : "outline"}
                        size="md"
                        py={{ base: 4, md: 5 }}
                        px={4}
                        height="auto"
                        whiteSpace="normal"
                        borderColor={formData.registration_type === "Company-Sponsored" ? "blue.500" : "gray.300"}
                    >
                        Company-Sponsored
                    </Button>
                </SimpleGrid>
            </FormControl>

            {formData.registration_type === "Company-Sponsored" && (
                <motion.div
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.3}}
                >
                    <FormControl mt={5} isInvalid={validationErrors.sponsor_uen}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">Sponsor Company Registration Number (UEN)</FormLabel>
                        <Input
                            value={formData.sponsor_uen || ''}
                            onChange={(e) => {
                                const uen = e.target.value.toUpperCase();
                                handleChange("sponsor_uen", uen);

                                // Validate UEN on change
                                const isValid = validateUEN(uen);
                                setValidationErrors(prev => ({
                                    ...prev,
                                    sponsor_uen: !isValid && uen !== "" ? "Invalid UEN format" : false
                                }));
                                setIsUENValid(isValid);
                                if (isValid) {
                                    trackGAEvent('Registration', 'uen_entered', 'valid_uen');
                                }
                            }}
                            placeholder="e.g., 201139596G"
                            size="sm"
                            type="text"
                            maxLength={10}
                            bg="white"
                            isInvalid={validationErrors.sponsor_uen}
                        />
                        {validationErrors.sponsor_uen && (
                            <Text color="red.500" fontSize="sm" mt={1}>
                                Please enter a valid UEN
                            </Text>
                        )}
                    </FormControl>

                    {isUENValid && (
                        <motion.div
                            initial={{opacity: 0, y: 20}}
                            animate={{opacity: 1, y: 0}}
                            transition={{duration: 0.3}}
                        >
                            <FormControl mt={5} isInvalid={validationErrors.isSME}>
                                <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">Is your company a
                                    SME?</FormLabel>
                                <RadioGroup onChange={setIsSME} value={isSME}>
                                    <HStack spacing="24px">
                                        <Radio value="yes">Yes</Radio>
                                        <Radio value="no">No</Radio>
                                    </HStack>
                                </RadioGroup>
                                <Text fontSize="sm" color="green.600" mt={1}>
                                    (To qualify as an SME, your company must have at least 30% local shareholding. In
                                    addition, your company's annual turnover must be less than S$100 million OR have an
                                    employment size of not more than 200 workers.)
                                </Text>
                                {validationErrors.isSME && (
                                    <Text color="red.500" fontSize="sm" mt={1}>
                                        Please select if your company is a SME
                                    </Text>
                                )}
                            </FormControl>

                            <FormControl mt={5}>
                                <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">Team
                                    Registration</FormLabel>
                                <Text fontSize="sm" color="green.600">
                                    Registering for a team? We can help with the bulk registration! Email us at{' '}
                                    <Text
                                        as="a"
                                        href="mailto:courses@sozcode.com"
                                        color="orange.500"
                                        textDecoration="underline"
                                        _hover={{color: 'orange.600'}}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        courses@sozcode.com
                                    </Text>
                                    {' '}:D
                                </Text>
                            </FormControl>
                        </motion.div>
                    )}
                </motion.div>
            )}
            </>
                );
            })()}

            {/* Nationality / Identification Type — rendered based on DB config.
                field_type='country'  → ISO 3166-1 searchable combobox (e.g. Lx Malaysia foreign students)
                field_type='select'   → tenant-configured options list
                no config / fallback  → hardcoded SG-specific dropdown (legacy behaviour) */}
            {(() => {
                if (!isSystemFieldInTemplate('nationality')) return null;
                const natConfig = getOriginalFieldConfig('nationality');
                if (natConfig?.is_hidden) return null;
                const label = natConfig?.field_label || 'Identification Type';
                const isRequired = natConfig ? natConfig.is_required !== false : true;

                if (natConfig?.field_type === 'country') {
                    const currentVal = countryOptions.find(
                        opt => opt.label === formData.nationality || opt.value === formData.nationality
                    ) || null;
                    return (
                        <FormControl mt={5}>
                            <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">
                                <BilingualLabel label={label} isRequired={isRequired} />
                            </FormLabel>
                            <RegistrationSearchableSelect
                                value={currentVal}
                                onChange={(option) => {
                                    const val = option ? option.label : '';
                                    trackGAEvent('Registration', 'nationality_selected', val);
                                    handleChange('nationality', val);
                                    trackFBEvent('trackCustom', 'FormStep_Nationality', { step_number: 3, nationality: val });
                                }}
                                options={countryOptions}
                                placeholder="Search and select country..."
                                isClearable
                            />
                        </FormControl>
                    );
                }

                const selectOptions = natConfig?.field_type === 'select' && natConfig?.field_options?.length
                    ? natConfig.field_options
                    : ['Singapore Citizen', 'Singapore Permanent Resident', 'Long Term Visitor Pass+ (LTVP+)', 'Non-Singapore Citizen/PR (FIN)', 'Non-Singapore Resident (Foreign Passport)'];

                return (
                    <FormControl mt={5}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">
                            <BilingualLabel label={label} isRequired={isRequired} />
                        </FormLabel>
                        <Select
                            name="nationality"
                            value={formData.nationality}
                            placeholder='Select option'
                            bg={"white"}
                            onChange={(e) => {
                                trackGAEvent('Registration', 'nationality_selected', e.target.value);
                                handleChange('nationality', e.target.value);
                                trackFBEvent('trackCustom', 'FormStep_Nationality', { step_number: 3, nationality: e.target.value });
                            }}
                        >
                            {selectOptions.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </Select>
                    </FormControl>
                );
            })()}

            {/* NRIC / ID Number — hidden or optional based on DB config.
                For SG tenants: required by default. For international tenants (e.g. Lx Malaysia):
                the DB row sets is_required=false so the asterisk and submit validation are skipped. */}
            {(() => {
                if (!isSystemFieldInTemplate('nric')) return null;
                const nricConfig = getOriginalFieldConfig('nric');
                if (nricConfig?.is_hidden) return null;
                const isNricRequired = nricConfig ? nricConfig.is_required !== false : true;
                const isSingaporeCitizenOrPR = formData.nationality === 'Singapore Citizen' || formData.nationality === 'Singapore Permanent Resident';
                const isFIN = formData.nationality === 'Non-Singapore Citizen/PR (FIN)' || formData.nationality === 'Long Term Visitor Pass+ (LTVP+)';
                const isPassport = formData.nationality === 'Non-Singapore Resident (Foreign Passport)';
                const idLabel = isPassport ? 'Passport Number' : isFIN ? 'FIN Number' : (nricConfig?.field_label || 'Personal Identification Number (NRIC)');
                const idPlaceholder = isPassport ? 'Enter passport number' : isFIN ? 'F1234567X' : 'S9912345Z';
                const idMaxLength = (isSingaporeCitizenOrPR || isFIN) ? 9 : undefined;
                return (
                    <FormControl mt={5}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">
                            <BilingualLabel label={idLabel} isRequired={isNricRequired} />
                            {!isNricRequired && <Text as="span" color="gray.400" fontSize="sm" fontWeight="normal" ml={2}>(optional)</Text>}
                        </FormLabel>
                        <Input
                            value={formData.nric || ''}
                            onChange={(e) => {
                                const nric = isSingaporeCitizenOrPR ? e.target.value.toUpperCase() : e.target.value;
                                handleChange('nric', nric);
                            }}
                            onBlur={(e) => {
                                const nric = e.target.value;
                                if (!nric) return;
                                const isValid = validateNRIC(nric, formData.nationality);
                                setValidationErrors(prev => ({
                                    ...prev,
                                    nric: !isValid && nric !== ''
                                        ? (isSingaporeCitizenOrPR
                                            ? 'Invalid NRIC / NRIC format'
                                            : isPassport
                                                ? 'Please enter your passport number'
                                                : isFIN ? 'Please enter your FIN number' : false)
                                        : false
                                }));
                                if (isValid) {
                                    trackGAEvent('Registration', 'nric_entered', 'valid_nric');
                                    trackFBEvent('trackCustom', 'FormStep_NRIC', { step_number: 3.5, valid: true });
                                }
                            }}
                            placeholder={idPlaceholder}
                            size="md"
                            type="text"
                            maxLength={idMaxLength}
                            bg="white"
                            isInvalid={validationErrors.nric}
                        />
                        {validationErrors.nric && (
                            <Text color="red.500" fontSize="sm" mt={1}>
                                {validationErrors.nric}
                            </Text>
                        )}
                    </FormControl>
                );
            })()}

            {isSystemFieldInTemplate('dob') && (
                <FormControl mt={5}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">
                        <BilingualLabel label={getOriginalFieldConfig('dob')?.field_label || 'Date of Birth'} isRequired />
                    </FormLabel>
                        <Input
                            value={formData.displayDob || ''}
                            onChange={(e) => {
                                const inputDate = e.target.value;
                                handleChange("displayDob", inputDate);

                                const date = new Date(inputDate);
                                if (!isNaN(date)) {
                                    const storedFormat = date.toISOString().split('T')[0];
                                    const age = calculateAge(storedFormat);
                                    handleChange("dob", storedFormat);
                                    handleChange("birth_age", age);
                                }
                            }}
                            onBlur={(e) => {
                                const inputDate = e.target.value;
                                if (!inputDate) {
                                    setValidationErrors(prev => ({...prev, dob: "Date of birth is required"}));
                                    return;
                                }

                                const date = new Date(inputDate);
                                if (isNaN(date)) {
                                    setValidationErrors(prev => ({...prev, dob: "Invalid date"}));
                                    return;
                                }

                                const age = calculateAge(date);
                                if (age < 0 || age > 150) {
                                    setValidationErrors(prev => ({...prev, dob: "Invalid date of birth"}));
                                    return;
                                }

                                // Catalog minimum age (course_type.minimum_age); blank/null = no restriction
                                const selectedRun =
                                    (selectedCourseRunIds?.length
                                        ? courseRuns.find(c => c.id === selectedCourseRunIds[0])
                                        : null) ||
                                    courseRuns.find(c => c.id === formData.course_run_id || c.course_type_code === formData.course_type_code);
                                const minAgeRaw = selectedRun?.course_type?.minimum_age;
                                const minAge = minAgeRaw != null && minAgeRaw !== '' ? Number(minAgeRaw) : null;
                                if (minAge != null && Number.isFinite(minAge) && age < minAge) {
                                    setValidationErrors(prev => ({
                                        ...prev,
                                        dob: `You must be at least ${minAge} years old to register`
                                    }));
                                    return;
                                }

                                setValidationErrors(prev => ({...prev, dob: false}));
                                trackGAEvent('Registration', 'dob_entered', String(age));

                                if (!validationErrors.dob) {
                                    trackFBEvent('trackCustom', 'FormStep_DOB', {
                                        step_number: 4,
                                        age_group: age >= 40 ? "40+" : "Below 40"
                                    });
                                }
                            }}
                            placeholder="Select date"
                            size="md"
                            type="date"
                            bg="white"
                            isInvalid={validationErrors.dob}
                        />
                        {validationErrors.dob && (
                            <Text color="red.500" fontSize="sm" mt={1}>
                                {validationErrors.dob}
                            </Text>
                        )}
                    </FormControl>
            )}

            {isSystemFieldInTemplate('participant_name') && (
                <FormControl mt={5}>
                    {(() => {
                        const nameConfig = getOriginalFieldConfig('participant_name');
                        const nameLabel = nameConfig?.field_label || 'Full Name';
                        const nameRequired = nameConfig ? nameConfig.is_required !== false : true;
                        return (
                            <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">
                                {nameRequired
                                    ? <BilingualLabel label={nameLabel} isRequired />
                                    : <><BilingualLabel label={nameLabel} isRequired={false} /><Text as="span" color="gray.500" fontSize="sm" ml={1}>(optional)</Text></>
                                }
                            </FormLabel>
                        );
                    })()}
                        <Input
                            value={formData.participant_name || ''}
                            onChange={(e) => handleChange("participant_name", e.target.value)}
                            onBlur={(e) => {
                                trackGAEvent('Registration', 'name_entered', 'name_provided');
                                handleChange("participant_name", e.target.value);

                                if (e.target.value) {
                                    trackFBEvent('trackCustom', 'FormStep_Name', {
                                        step_number: 5
                                    });
                                }
                            }}
                            size="md"
                            type="text"
                            bg="white"
                        />
                    </FormControl>
            )}

            {isSystemFieldInTemplate('participant_email') && (
                <FormControl mt={5} isInvalid={validationErrors.email || validationErrors.invalidDomain}>
                    <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">
                        <BilingualLabel label={getOriginalFieldConfig('participant_email')?.field_label || 'Participant Email'} isRequired />
                        {existingRegistration && !emailEditable && (
                            <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal" ml={2}>
                                (double-click to change — requires re-verification)
                            </Text>
                        )}
                    </FormLabel>
                        <Input
                            value={formData.participant_email || ''}
                            onChange={(e) => emailEditable && handleChange("participant_email", e.target.value)}
                            onDoubleClick={() => {
                                if (existingRegistration && !emailEditable) {
                                    setEmailEditable(true);
                                    if (setEmailVerified) setEmailVerified(false);
                                }
                            }}
                            onBlur={(e) => {
                                if (!emailEditable) return;
                                const email = e.target.value;
                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                const hasCoDomain = email.endsWith('.co') && !email.endsWith('.com');
                                const hasInvalidDomain = /\.\w$/.test(email);

                                setValidationErrors(prev => ({
                                    ...prev,
                                    email: !emailRegex.test(email) && email !== "",
                                    emailSuggestion: hasCoDomain,
                                    invalidDomain: hasInvalidDomain
                                }));

                                if (!validationErrors.email && !validationErrors.invalidDomain && e.target.value) {
                                    trackFBEvent('trackCustom', 'FormStep_Email', {
                                        step_number: 6
                                    });
                                }
                            }}
                            size="md"
                            type="email"
                            bg={emailEditable || !existingRegistration ? "white" : "gray.50"}
                            isReadOnly={existingRegistration && !emailEditable}
                            cursor={existingRegistration && !emailEditable ? "default" : "text"}
                            title={existingRegistration && !emailEditable ? "Double-click to edit (requires re-verification)" : undefined}
                            isInvalid={validationErrors.email || validationErrors.invalidDomain}
                        />
                        {validationErrors.email && (
                            <Text color="red.500" fontSize="sm" mt={1}>
                                Please enter a valid email address
                            </Text>
                        )}
                        {validationErrors.emailSuggestion && (
                            <Text color="yellow.600" fontSize="sm" mt={1}>
                                Do you mean {formData.participant_email.replace(/\.co$/, '.com')}?
                            </Text>
                        )}
                        {validationErrors.invalidDomain && (
                            <Text color="red.500" fontSize="sm" mt={1}>
                                Invalid domain - domain must be at least 2 characters
                            </Text>
                        )}
                    </FormControl>
            )}

            {(() => {
                if (!isSystemFieldInTemplate('mobile_no')) return null;
                const mobileConfig = getOriginalFieldConfig('mobile_no');
                if (mobileConfig?.is_hidden) return null;
                const mobileRequired = mobileConfig ? mobileConfig.is_required : true;
                const mobileLabel = mobileConfig?.field_label || 'Mobile Number';
                return (
                    <FormControl mt={5} isInvalid={validationErrors.mobile}>
                        <FormLabel mb={2} fontWeight="bold" color="black" fontSize="md">
                            {mobileRequired
                                ? <BilingualLabel label={mobileLabel} isRequired />
                                : <><BilingualLabel label={mobileLabel} isRequired={false} /><Text as="span" color="gray.500" fontSize="sm" ml={1}>(optional)</Text></>
                            }
                        </FormLabel>
                        <PhoneInput
                            international
                            defaultCountry="SG"
                            value={formData.mobile_no || ''}
                            onChange={(value) => {
                                handleChange("mobile_no", value || '');
                                
                                if (value) {
                                    try {
                                        const isValid = isValidPhone(value);
                                        setValidationErrors(prev => ({
                                            ...prev,
                                            mobile: !isValid
                                        }));

                                        if (isValid) {
                                            const phoneNumber = parsePhone(value);
                                            const formattedNumber = phoneNumber.format('INTERNATIONAL');
                                            const numberOnly = phoneNumber.nationalNumber;

                                            setFormData(prevData => ({
                                                ...prevData,
                                                mobile_no: formattedNumber,
                                                mobile_no_raw: numberOnly
                                            }));

                                            trackGAEvent('Registration', 'phone_number_entered', 'valid_number');
                                            trackFBEvent('trackCustom', 'FormStep_Mobile', {
                                                step_number: 7
                                            });
                                        } else {
                                            trackGAEvent('Registration', 'phone_number_entered', 'invalid_number');
                                        }
                                    } catch (error) {
                                        setValidationErrors(prev => ({...prev, mobile: true}));
                                        trackGAEvent('Registration', 'phone_number_entered', 'error');
                                    }
                                } else {
                                    setValidationErrors(prev => ({...prev, mobile: false}));
                                }
                            }}
                            inputComponent={Input}
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            className="chakra-phone-input"
                        />
                        {validationErrors.mobile && (
                            <Text color="red.500" fontSize="sm" mt={1}>
                                Please enter a valid mobile number
                            </Text>
                        )}
                    </FormControl>
                );
            })()}

            {/* Additional Fields Section */}
            <Box mt={8} p={6} borderWidth="1px" borderRadius="lg" borderColor="brand.200" bg="brand.50">
                <AdditionalFields
                    formData={formData}
                    setFormData={setFormData}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    registrationId={registrationId}
                    setRegistrationId={setRegistrationId}
                    isReadOnly={false}
                    templateFieldIds={templateFieldIds}
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    uploadingFiles={uploadingFiles}
                    setUploadingFiles={setUploadingFiles}
                    onAutoSave={async () => {
                        // Auto-save a draft registration if needed for file uploads
                        if (!registrationId && formData.participant_name && formData.participant_email && formData.course_run_id) {
                            try {
                                const draftPayload = {
                                    participant_name: formData.participant_name,
                                    participant_email: formData.participant_email,
                                    draft_course_run_code: formData.course_run_id,
                                    registration_stage: 'draft',
                                    registration_type: 'course',
                                    additional_data: formData.additional_data || {}
                                };

                                const { data, error } = await supabase
                                    .from('registration')
                                    .insert([draftPayload])
                                    .select()
                                    .single();

                                if (error) throw error;
                                if (data?.id) {
                                    setRegistrationId(data.id);
                                    return data.id;
                                }
                            } catch (error) {
                                console.error('Error creating draft registration for file upload:', error);
                                toast({
                                    title: "Could not save draft for file upload",
                                    description: error?.message || "Please try again or refresh the page.",
                                    status: "error",
                                    duration: 5000,
                                    isClosable: true,
                                });
                                return null;
                            }
                        }
                        return registrationId;
                    }}
                />
            </Box>

            {/* Installment Schedule Table */}
            {(() => {
                // Get installment scheme value using nested field access
                const installmentScheme = formData.payment?.installment_scheme;
                console.log('Installment scheme check:', {
                    installmentScheme,
                    hasScheme: !!installmentScheme,
                    isNotOther: installmentScheme !== 'Other',
                    formDataPayment: formData.payment
                });
                return installmentScheme && installmentScheme !== 'Other';
            })() && (
                <Box mt={5} p={4} borderWidth="1px" borderRadius="lg" borderColor="blue.200" bg="blue.50">
                    <Heading size="md" mb={4} color="blue.700">
                        📅 Installment Schedule
                    </Heading>
                    {(() => {
                        // Get installment months from the selected scheme
                        const installmentScheme = formData.payment?.installment_scheme;
                        const months = parseInt(installmentScheme.split(' ')[0]);
                        
                        console.log('Installment calculation:', {
                            installmentScheme,
                            months,
                            formData: formData
                        });
                        
                        // Calculate total cost
                        let totalCost = 0;
                        let startDate = null;
                        
                        const params = new URLSearchParams(location.search);
                        const fullCertification = params.get('full_certification');
                        
                        if (fullCertification) {
                            // For full certifications, get cost directly from full_certification table
                            // and find earliest start date among selected courses
                            const selectedCourses = courseRuns.filter(course => 
                                selectedCourseRunIds.includes(course.id)
                            );
                            
                            if (selectedCourses.length > 0) {
                                // Find earliest start date
                                const dates = selectedCourses.map(course => new Date(course.start_date));
                                startDate = new Date(Math.min(...dates));
                                
                                // Use the fetched full certification cost
                                totalCost = fullCertificationCost || 0;
                                
                                console.log('Full certification cost calculation:', {
                                    fullCertificationId: fullCertification,
                                    selectedCourses: selectedCourses.length,
                                    fullCertificationCost,
                                    totalCost
                                });
                            }
                        } else {
                            // For individual courses
                            const selectedCourse = courseRuns.find(course => course.id === formData.course_run_id);
                            if (selectedCourse) {
                                totalCost = parseFloat(selectedCourse.price || 0);
                                startDate = new Date(selectedCourse.start_date);
                            }
                        }
                        
                        console.log('Cost calculation result:', {
                            totalCost,
                            startDate,
                            fullCertification,
                            selectedCourseRunIds,
                            courseRuns: courseRuns.length
                        });
                        
                        if (totalCost === 0 || !startDate) {
                            return (
                                <Text color="gray.600" fontSize="sm">
                                    Please select a course to view installment schedule. (Debug: Cost={totalCost}, StartDate={startDate ? startDate.toISOString() : 'null'})
                                </Text>
                            );
                        }
                        
                        // Calculate GST if enabled
                        const gstRate = orgGstSettings?.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
                        const gstAmount = totalCost * gstRate;
                        const totalWithGst = totalCost + gstAmount;
                        
                        // Calculate installment amount (including GST)
                        const installmentAmount = Math.round(totalWithGst / months);
                        const remainder = totalWithGst - (installmentAmount * months);
                        
                        // Generate installment schedule
                        const installments = [];
                        for (let i = 0; i < months; i++) {
                            const dueDate = new Date(startDate);
                            dueDate.setMonth(dueDate.getMonth() + i);
                            
                            // Add remainder to the last installment
                            const amount = i === months - 1 ? installmentAmount + remainder : installmentAmount;
                            
                            installments.push({
                                installment: `${i + 1}${i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'} Installment`,
                                amount: amount,
                                dueDate: dueDate
                            });
                        }
                        
                        return (
                            <Box overflowX="auto">
                                <Table variant="simple" size="sm" bg="white" borderRadius="md">
                                    <Thead bg="blue.100">
                                        <Tr>
                                            <Th fontSize="sm" fontWeight="bold" color="blue.700">Installment</Th>
                                            <Th fontSize="sm" fontWeight="bold" color="blue.700" isNumeric>
                                                {orgGstSettings?.gst_enabled 
                                                    ? `Amount (incl. ${orgGstSettings.gst_rate}% GST) (S$)` 
                                                    : 'Amount (S$)'}
                                            </Th>
                                            <Th fontSize="sm" fontWeight="bold" color="blue.700">Date Due</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {installments.map((installment, index) => (
                                            <Tr key={index} _hover={{ bg: "blue.25" }}>
                                                <Td fontSize="sm" fontWeight="medium">{installment.installment}</Td>
                                                <Td fontSize="sm" fontWeight="bold" isNumeric color="green.600">
                                                    S$ {installment.amount.toLocaleString()}
                                                </Td>
                                                <Td fontSize="sm">
                                                    {installment.dueDate.toLocaleDateString('en-GB', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                                <Text fontSize="xs" color="gray.600" mt={2}>
                                    Course Fee: S$ {totalCost.toLocaleString()}
                                    {orgGstSettings?.gst_enabled && (
                                        <> + GST ({orgGstSettings.gst_rate}%): S$ {gstAmount.toLocaleString()} = <strong>S$ {totalWithGst.toLocaleString()}</strong></>
                                    )}
                                </Text>
                            </Box>
                        );
                    })()}
                </Box>
            )}

            <Box mt={5} p={4} borderWidth="1px" borderRadius="lg" borderColor="brand.300" bg="white">
                        {(() => {
                            const selectedCourse = courseRuns.find(course => course.id === formData.course_run_id);
                            console.log('Selected course individual module:', selectedCourse);
                            if (!selectedCourse) return null;

                            const originalPrice = selectedCourse.course_type.price;
                            const discountAmount = getDiscountAmount(selectedCourse);
                            const discountedPrice = originalPrice - discountAmount;
                            const nettPrice = calculatePrice(selectedCourse.course_type);
                            const subsidyAmount = discountedPrice - nettPrice;
                            const gstRate = orgGstSettings?.gst_enabled ? (orgGstSettings.gst_rate / 100) : 0;
                            const gstAmount = Math.round(nettPrice * gstRate * 100) / 100;
                            const nettPriceWithGst = nettPrice + gstAmount;

                            return (
                                <>
                                    {/* Only show subsidy message if it's an SSG course */}
                                    {selectedCourse.ssg_course && formData.registration_type !== "Company-Sponsored" && (
                                        <Text fontSize="md" color="green.600" mb={3}>
                                            {formData.nationality === "Singapore Citizen" ?
                                                parseInt(formData.birth_age) >= 40 ?
                                                    "As a Singapore Citizen aged 40 and above, you qualify for a 70% subsidy." :
                                                    "As a Singapore Citizen aged below 40, you qualify for a 50% subsidy." :
                                                formData.nationality === "Singapore Permanent Resident" ?
                                                    "As a Singapore PR, you qualify for a 50% subsidy." :
                                                    undefined}
                                        </Text>
                                    )}

                                    {/* Only show subsidy message for company-sponsored if it's an SSG course */}
                                    {selectedCourse.ssg_course && formData.registration_type === "Company-Sponsored" &&
                                        (formData.nationality === "Singapore Citizen" || formData.nationality === "Singapore Permanent Resident") && (
                                            <Text fontSize="md" color="green.600" mb={3}>
                                                {isSME === "yes"
                                                    ? `As a ${formData.nationality === "Singapore Citizen" ? "Singaporean" : "PR"} employee of a SME, you qualify for 70% subsidy.`
                                                    : isSME === "no"
                                                        ? formData.nationality === "Singapore Citizen"
                                                            ? parseInt(formData.birth_age) >= 40
                                                                ? "As a Singapore Citizen aged 40 and above, you qualify for a 70% subsidy."
                                                                : "As a Singapore Citizen aged below 40, you qualify for a 50% subsidy."
                                                            : "As a Singapore PR, you qualify for a 50% subsidy."
                                                        : "Qualifies for up to 70% subsidy at any age if your company is an SME."}
                                            </Text>
                                        )}

                                    <Heading size="xxs" mb={3} color="brand.700">Price Calculation
                                        ({selectedCourse.name})</Heading>
                                    
                                    
                                    <HStack justify="space-between" mb={2}>
                                        <Text fontSize="md" color="brand.500">Original Price:</Text>
                                        <Text fontSize="md" color="brand.700">SGD {originalPrice}</Text>
                                    </HStack>

                                    {/* Show early bird discount if available */}
                                    {discountAmount > 0 && (
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontSize="md" color="orange.500">Early Bird Discount:</Text>
                                            <Text fontSize="md" color="orange.600">-SGD {discountAmount}</Text>
                                        </HStack>
                                    )}

                                    {/* Only show subsidy amount if it's an SSG course and there is a subsidy */}
                                    {selectedCourse.ssg_course && subsidyAmount > 0 && (
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontSize="md" color="brand.500">Subsidy:</Text>
                                            <Text fontSize="md" color="green.600">SGD {subsidyAmount}</Text>
                                        </HStack>
                                    )}

                                    <Divider mb={2} borderColor="brand.300"/>

                                    {/* Subtotal row (before GST) — only show label if GST applies */}
                                    {orgGstSettings?.gst_enabled && (
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontSize="md" color="brand.500">
                                                {selectedCourse.ssg_course ? "Nett Price (before GST):" : "Subtotal (before GST):"}
                                            </Text>
                                            <Text fontSize="md" color="brand.700">SGD {nettPrice}</Text>
                                        </HStack>
                                    )}

                                    {/* GST row */}
                                    {orgGstSettings?.gst_enabled && (
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontSize="md" color="brand.500">GST ({orgGstSettings.gst_rate}%):</Text>
                                            <Text fontSize="md" color="brand.700">SGD {gstAmount}</Text>
                                        </HStack>
                                    )}

                                    <Divider mb={2} borderColor="brand.300"/>
                                    <HStack justify="space-between">
                                        <Text fontWeight="bold" fontSize="lg" color="brand.700">
                                            {selectedCourse.ssg_course
                                                ? `Nett Price${orgGstSettings?.gst_enabled ? ` (incl. ${orgGstSettings.gst_rate}% GST)` : ''} :`
                                                : `Course Fee${orgGstSettings?.gst_enabled ? ` (incl. ${orgGstSettings.gst_rate}% GST)` : ''} :`}
                                        </Text>
                                        <Text fontWeight="bold" fontSize="2xl">
                                            SGD {orgGstSettings?.gst_enabled ? nettPriceWithGst : nettPrice}
                                        </Text>
                                    </HStack>
                                </>
                            );
                        })()}
                    </Box>
                </>
            )}
        </Box>
    );
};

// Specialized card component for diploma modules with calendar
function DiplomaModuleCard(props) {
    const {getInputProps, getRadioProps} = useRadio(props)
    const {course_name, start_date, end_date, course_dates, early_bird_discount, early_bird_end_date, is_course_run_confirmed, course_type_code, module_tag, is_diploma_module, isDisabled} = props
    const input = getInputProps()
    const checkbox = getRadioProps()

    return (
        <Box as='label' w="100%" mb={4}>
            <input {...input} />
            <Box
                {...checkbox}
                cursor={isDisabled ? 'default' : 'pointer'}
                borderWidth='2px'
                borderRadius='lg'
                boxShadow='md'
                borderColor={isDisabled ? "green.300" : "brand.300"}
                bg={isDisabled ? "green.50" : "white"}
                _checked={{
                    bg: "brand.50",
                    color: 'black',
                    borderColor: "brand.600",
                    boxShadow: "lg"
                }}
                _hover={isDisabled ? {} : {
                    borderColor: "brand.500",
                    boxShadow: "lg"
                }}
               
                px={3}
                py={3}
                wordBreak="break-word"
                h="100%"
                display="flex"
                flexDirection="column"
            >
                <HStack justify="space-between" mb={2}>
                    <HStack spacing={2}>
                        <Badge colorScheme="blue" fontSize="xs" px={2} py={1}>
                            {course_type_code}
                        </Badge>
                        {module_tag && (
                            <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
                                {module_tag.charAt(0).toUpperCase() + module_tag.slice(1)}
                            </Badge>
                        )}
                        {isDisabled && (
                            <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
                                ✓ Included
                            </Badge>
                        )}
                    </HStack>
                    {early_bird_end_date > moment().format("YYYY-MM-DD") && (
                        <Text fontSize="10px" color="green.600" fontWeight="bold">
                            Early Bird ${early_bird_discount}
                        </Text>
                    )}
                </HStack>
                
                <Text fontSize="sm" fontWeight="700" mb={2} color="brand.700">
                    {course_name}
                </Text>
                
                <HStack mb={2} fontSize="xs" color="gray.600" justify="space-between">
                    <Text>📅 {moment(start_date).format("DD MMM")} - {moment(end_date).format("DD MMM YYYY")}</Text>
                    {course_dates && course_dates.length > 0 && (
                        <Badge colorScheme="purple" fontSize="10px">
                            {(() => {
                                const totalDuration = course_dates.reduce((sum, session) => {
                                    const startTime = moment(session.start_time, "HHmm");
                                    const endTime = moment(session.end_time, "HHmm");
                                    return sum + endTime.diff(startTime, 'hours', true);
                                }, 0);
                                const totalHours = Math.floor(totalDuration);
                                const totalMinutes = Math.round((totalDuration % 1) * 60);
                                return `${totalHours}h ${totalMinutes}m`;
                            })()}
                        </Badge>
                    )}
                </HStack>

                {/* Calendar component */}
                <ModuleCalendar course_dates={course_dates} moduleCode={course_type_code} />
            </Box>
        </Box>
    )
}

function RadioCard(props) {
    const {getInputProps, getRadioProps} = useRadio(props)
    const {course_name, start_date, end_date, course_dates, early_bird_discount, early_bird_end_date, is_course_run_confirmed, course_type_code, is_diploma_module} = props
    const input = getInputProps()
    const checkbox = getRadioProps()

    return (
        <Box as='label' w="100%" mb={4}>
            <input {...input} />
            <Box
                {...checkbox}
                cursor='pointer'
                borderWidth='1px'
                borderRadius='md'
                boxShadow='md'
                borderColor="brand.300"
                _checked={{
                    bg: "brand.100",
                    color: 'black',
                    borderColor: "brand.700",
                }}
                _hover={{
                    borderColor: "brand.400",
                }}
               
                px={{base: 3, md: 4}}
                py={4}
                wordBreak="break-word"
                h="100%"
                display="flex"
                flexDirection="column"
            >
                <HStack justify="space-between" mb={2}>
                    {early_bird_end_date > moment().format("YYYY-MM-DD") && (
                        <Text fontSize="xs" color="green.600" fontWeight="bold">Early Bird Discount of ${early_bird_discount} if you register today!</Text>
                    )}
                    {is_diploma_module && (
                        <Badge colorScheme="blue" fontSize="xs">
                            {course_type_code}
                        </Badge>
                    )}
                </HStack>
                <Text fontSize={{base: "sm", md: "sm"}} fontWeight="600" mb={4}>
                    {course_name}
                </Text>
                <Text fontSize={{base: "xs", md: "xs"}}>
                    Start: {moment(start_date).format("DD MMM YYYY")} ({moment(start_date).format("ddd")})
                </Text>
                <Text fontSize={{base: "xs", md: "xs"}} mb={3}>
                    End: {moment(end_date).format("DD MMM YYYY")} ({moment(end_date).format("ddd")})
                </Text>

                {course_dates?.length > 0 && (
                                            <Box mt={2} borderTop="1px solid"
                             borderColor={checkbox._checked ? "whiteAlpha.300" : "brand.300"} pt={2}>
                        <Text fontSize={{base: "xs", md: "xs"}} fontWeight="600" mb={2}>
                            Schedule:
                        </Text>
                        <Box overflowX="auto" maxWidth="100%">
                            <Table variant="simple" size="sm" bg="transparent"
                                   style={{tableLayout: "fixed", width: "100%"}}>
                                <thead>
                                <tr>
                                    <th style={{fontSize: "12px", padding: "4px", textAlign: "left", width: "50%"}}>
                                        <Text fontSize="xs">Date</Text>
                                    </th>
                                    <th className="text-right" style={{fontSize: "12px", padding: "4px", textAlign: "right", width: "50%"}}>
                                        <Text fontSize="xs">Time</Text>
                                    </th>
                                </tr>
                                </thead>
                                <Tbody>
                                    {course_dates?.map((session, index) => {
                                        const sessionDate = moment(session.date);
                                        return (
                                            <Tr key={index}>
                                                <Td fontSize={{base: "xs", md: "13px"}} p={1} bg="transparent"
                                                    border="none">
                                                    <Text fontSize="xs">{sessionDate.format("DD MMM")} ({sessionDate.format("ddd")})</Text>
                                                </Td>
                                                <Td fontSize={{base: "xs", md: "13px"}} p={1} bg="transparent"
                                                    border="none" textAlign="right">
                                                    <Text fontSize="xs">{moment(session.start_time, "HHmm").format("h:mm A")} - {moment(session.end_time, "HHmm").format("h:mm A")}</Text>  
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

const Step2 = (props) => {
    const {
        courseRuns,
        course_run_id,
        setFormData,
        formData,
        allowedTab,
        setTabIndex,
        validationErrors,
        isTeamRegistration,
        calculatePrice,
        registrationId,
        setRegistrationId,
        checkRequiredAdditionalFields,
        getFieldValue,
        additionalFields,
        toast,
        selectedCourseRunIds,
        setSelectedCourseRunIds,
        selectedFiles,
        uploadingFiles,
        emailVerified,
        sendVerificationEmail,
        existingRegistration,
        needsEmailVerification = false,
        emailVerificationSent = false,
        isSendingVerification = false,
        onSendVerificationEmail,
        gateOnRunConfirmed = false,
        orgGstSettings,
        lxCopilotDirectoryIntake = false,
        directoryListingStatus = "idle",
        directoryChannelParam = "",
        templateFieldIds: stepTemplateFieldIds,
        onDirectoryIntakeSuccess,
        lxDirectoryFormReady = true,
    } = props
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showPayNowModal, setShowPayNowModal] = useState(false)
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
    const [isProcessingPayment, setIsProcessingPayment] = useState(false)
    const selectedCourse = courseRuns.find(course => course.id === formData.course_run_id);
    const nettPrice = selectedCourse ? calculatePrice(selectedCourse.course_type) : 0;
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const registration_type = params.get("registration_type")
    const navigate = useNavigate();

    const handleStripePayment = async () => {
        setIsProcessingPayment(true);
        try {
            // Prepared data for the edge function
            const requestData = {
                registration_id: registrationId,
                participant_email: formData.participant_email,
                participant_name: formData.participant_name,
                invoice_balance: 100, // Registration fee
                course_name: selectedCourse?.name || 'Course Registration',
                course_start_date: selectedCourse?.start_date 
                    ? moment(selectedCourse.start_date).format('DD MMM YYYY') 
                    : null
            };

            const { data, error } = await supabase.functions.invoke("create-registration-checkout", {
                body: requestData
            });

            if (error) throw error;
            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error("Checkout URL not found");
            }
        } catch (err) {
            console.error('Error with Stripe checkout:', err);
            toast({
                title: "Payment Error",
                description: "Could not initiate Stripe payment. Please try again or use PayNow.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const handlePayNowDone = async () => {
        setIsUpdatingStatus(true);
        try {
            // Update registration status to 'payment_in_progress'
            const { error: updateError } = await supabase
                .from('registration')
                .update({ registration_stage: 'payment_in_progress' })
                .eq('id', registrationId);

            if (updateError) throw updateError;

            toast({
                title: "Payment Recorded",
                    description: "Your registration status has been updated to 'payment in progress'. We will verify the payment shortly.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });

            navigate(`/register/${registrationId}`);
        } catch (err) {
            console.error('Error updating status:', err);
            toast({
                title: "Error",
                description: "Could not update your registration status. Please contact support.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsUpdatingStatus(false);
            setShowPayNowModal(false);
        }
    };

    // Helper function to check if a field is conditionally required
    const isFieldConditionallyRequired = (field) => {
        if (!field.conditional_requirements) {
            return false;
        }

        const condition = field.conditional_requirements.required_when;
        if (!condition) {
            return false;
        }

        const triggerField = condition.field;
        const triggerValue = getFieldValue(formData, triggerField);
        const operator = condition.operator;
        const expectedValue = condition.value;

        switch (operator) {
            case 'equals':
                return valuesEqualForConditionalRequirement(triggerValue, expectedValue);
            case 'contains':
                return (
                    Array.isArray(triggerValue) &&
                    triggerValue.some((tv) => valuesEqualForConditionalRequirement(tv, expectedValue))
                );
            case 'in':
                return (
                    Array.isArray(expectedValue) &&
                    expectedValue.some((ev) => valuesEqualForConditionalRequirement(triggerValue, ev))
                );
            case 'not_equals':
                return !valuesEqualForConditionalRequirement(triggerValue, expectedValue);
            default:
                return false;
        }
    };

    // Get effective requirement status (static or conditional)
    const getEffectiveRequiredStatus = (field) => {
        if (field.conditional_requirements) {
            return isFieldConditionallyRequired(field);
        }
        return field.is_required;
    };

    const handleChange = (name, value) => {
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };
    //const selected_course_run = courseRuns.filter((each) => each.attendance_id === course_run_id)[0]
    //const log_input_data = () => {console.log(formData)}
    const insertRegistration = async () => {
        trackGAEvent('Registration', 'submission_attempted', 'initiated');
        // First track submission attempt
        trackFBEvent('trackCustom', 'FormStep_SubmitAttempt', {
            step_number: 8
        });

        trackFBEvent('track', 'Lead', {
            content_name: 'Course Registration',
            content_category: formData.course_type_code
        });
        setIsSubmitting(true)
        try {
            console.log('Starting registration process...');
            console.log('Form data:', formData);

            // ── LX Copilot directory intake → directory_participant_draft (TMS RPC) ──
            if (lxCopilotDirectoryIntake) {
                if (!lxDirectoryFormReady) {
                    toast({
                        title: "Incomplete form",
                        description: "Please fill in all required fields before submitting.",
                        status: "warning",
                        duration: 6000,
                        isClosable: true,
                    });
                    setIsSubmitting(false);
                    return;
                }
                if (directoryListingStatus !== "valid") {
                    toast({
                        title: "Course not available",
                        description:
                            directoryListingStatus === "checking"
                                ? "Still verifying this course link. Please wait a moment."
                                : "This course is not available from the directory, or the link is out of date.",
                        status: "error",
                        duration: 7000,
                        isClosable: true,
                    });
                    setIsSubmitting(false);
                    return;
                }
                if (!checkRequiredAdditionalFields()) {
                    toast({
                        title: "Please complete required fields",
                        description: "Check highlighted sections below.",
                        status: "warning",
                        duration: 5000,
                        isClosable: true,
                    });
                    setIsSubmitting(false);
                    return;
                }
                const pEarly = new URLSearchParams(location.search);
                if (pEarly.get("full_certification") && selectedCourseRunIds.length > 0 && !formData.certification_run_id) {
                    toast({
                        title: "Missing certification run",
                        description: "Please select your certification run and modules, then try again.",
                        status: "error",
                        duration: 6000,
                        isClosable: true,
                    });
                    setIsSubmitting(false);
                    return;
                }
                const participantData = buildDirectoryParticipantPayload({
                    formData,
                    getFieldValue,
                    additionalFields,
                    templateFieldIds: stepTemplateFieldIds,
                });
                const p = new URLSearchParams(location.search);
                const fullCertification = p.get("full_certification");
                let submitResult;
                if (fullCertification && selectedCourseRunIds.length > 0) {
                    submitResult = await submitDirectoryParticipantDraftsFullCertModules(supabase, {
                        certRunCode: formData.certification_run_id,
                        directoryChannel: directoryChannelParam,
                        entryChannel: "lxcopilot",
                        participantData,
                        moduleRunCodes: selectedCourseRunIds,
                    });
                } else {
                    submitResult = await submitDirectoryParticipantDraft(supabase, {
                        runKind: "course_run",
                        runCode: formData.course_run_id,
                        directoryChannel: directoryChannelParam,
                        entryChannel: "lxcopilot",
                        participantData,
                    });
                }
                if (submitResult.ok) {
                    trackGAEvent("Registration", "directory_intake_success", "lxcopilot");
                    trackFBEvent("trackCustom", "DirectoryIntake_Submit", { status: true });
                    let directoryCourseName = "";
                    let directoryRunCode = "";
                    if (fullCertification && selectedCourseRunIds.length > 0) {
                        const labels = selectedCourseRunIds.map((id) => {
                            const cr = courseRuns.find((c) => c.id === id);
                            return cr?.course_type?.name || cr?.name || id;
                        }).filter(Boolean);
                        directoryCourseName =
                            labels.length > 1 ? `Certification programme (${labels.length} modules)` : labels[0] || "";
                        directoryRunCode = selectedCourseRunIds.join(", ");
                    } else {
                        const sc = courseRuns.find((c) => c.id === formData.course_run_id);
                        directoryCourseName = sc?.course_type?.name || sc?.name || "";
                        directoryRunCode = formData.course_run_id || "";
                    }
                    try {
                        const { error: dirEmailErr } = await supabase.functions.invoke(
                            "send-directory-intake-received-email",
                            {
                                body: {
                                    participantEmail: formData.participant_email,
                                    participantName: formData.participant_name,
                                    courseName: directoryCourseName,
                                    courseRunCode: directoryRunCode,
                                },
                            }
                        );
                        if (dirEmailErr) {
                            console.error("send-directory-intake-received-email:", dirEmailErr);
                        }
                    } catch (dirEmailEx) {
                        console.error("send-directory-intake-received-email:", dirEmailEx);
                    }
                    onDirectoryIntakeSuccess?.();
                } else {
                    toast({
                        title: "Could not submit application",
                        description:
                            submitResult.error ||
                            "Please try again or contact your training provider.",
                        status: "error",
                        duration: 7000,
                        isClosable: true,
                    });
                    trackGAEvent("Registration", "directory_intake_failed", submitResult.error || "unknown");
                }
                setIsSubmitting(false);
                return;
            }
            
            // Get the selected course to calculate discount
            const selectedCourse = courseRuns.find(course => course.id === formData.course_run_id);
            console.log('Selected course:', selectedCourse);
            console.log('Form course_run_id (text code):', formData.course_run_id);
            console.log('Selected course id (for registration):', selectedCourse?.id);

            // ── EMAIL VERIFICATION GATE ──────────────────────────────────────
            // Only applies to standalone courses (full cert handles this itself)
            const params_check = new URLSearchParams(location.search);
            const isFullCert = !!params_check.get('full_certification');
            if (!isFullCert) {
                const earlyPolicy = await getEffectivePolicy(
                    supabase, formData.course_type_code, formData.course_run_id
                );
                if (earlyPolicy.needs_email_verification && !emailVerified) {
                    if (!registrationId) {
                        // Create a minimal draft to anchor the verification link
                        const { data: draft, error: draftErr } = await supabase
                            .from('registration')
                            .insert([{
                                participant_name: formData.participant_name,
                                participant_email: formData.participant_email,
                                draft_course_run_code: selectedCourse?.id || null,
                                registration_stage: 'draft',
                            }])
                            .select('id')
                            .single();
                        if (draftErr) throw draftErr;
                        setRegistrationId(draft.id);
                        await sendVerificationEmail(draft.id, formData.participant_email, formData.participant_name);
                        toast({
                            title: "Verify your email first",
                            description: `A verification link has been sent to ${formData.participant_email}. Click it, then come back and submit again.`,
                            status: "info",
                            duration: 10000,
                            isClosable: true,
                        });
                    } else {
                        toast({
                            title: "Email not yet verified",
                            description: "Please check your inbox and click the verification link before submitting.",
                            status: "warning",
                            duration: 5000,
                            isClosable: true,
                        });
                    }
                    setIsSubmitting(false);
                    return;
                }
            }
            // ────────────────────────────────────────────────────────────────

            const discountAmount = getDiscountAmount(selectedCourse);
            console.log('Discount amount:', discountAmount);

            // Prepare additional data from form fields
            const additionalData = {};
            
            // Get all additional fields from the database to know which fields to include
            console.log('Fetching additional fields...');
            // Name must not be `additionalFields` — that would shadow the Step2 prop and TDZ-break
            // the LX directory branch above which uses the prop in buildDirectoryParticipantPayload.
            const { data: nonOriginalFieldKeyRows, error: fieldsError } = await supabase
                .from('additional_registration_fields')
                .select('field_key')
                .or('field_status.is.null,field_status.eq.active')
                .eq('is_original_field', false);

            if (fieldsError) {
                console.error('Error fetching additional fields:', fieldsError);
                throw new Error(`Failed to fetch additional fields: ${fieldsError.message}`);
            }

            console.log('Additional fields:', nonOriginalFieldKeyRows);

            // Extract additional field values from formData
            if (nonOriginalFieldKeyRows) {
                nonOriginalFieldKeyRows.forEach(field => {
                    const fieldValue = getFieldValue(formData, field.field_key);
                    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
                        // Handle nested field keys (e.g., "personal_details.gender")
                        const keys = field.field_key.split('.');
                        if (keys.length > 1) {
                            // Create nested structure
                            let current = additionalData;
                            for (let i = 0; i < keys.length - 1; i++) {
                                if (!current[keys[i]]) {
                                    current[keys[i]] = {};
                                }
                                current = current[keys[i]];
                            }
                            current[keys[keys.length - 1]] = fieldValue;
                        } else {
                            additionalData[field.field_key] = fieldValue;
                        }
                    }
                });
            }

            // Add declaration date (today's date) to additional data
            const today = new Date().toISOString().split('T')[0];
            if (!additionalData.legal) {
                additionalData.legal = {};
            }
            additionalData.legal.declaration_date = today;

            // Note: Signature will be collected later in the process

            // Check if this is a full certification registration
            const params = new URLSearchParams(location.search);
            const fullCertification = params.get('full_certification');
            
            let registrationData;
            let data, error;
            
            if (fullCertification && selectedCourseRunIds.length > 0) {
                // Create multiple registrations for full certification modules
                console.log('Creating multiple registrations for full certification modules:', selectedCourseRunIds);
                console.log('Available course runs:', courseRuns.length);
                
                const registrationPromises = selectedCourseRunIds.map(async (courseRunId) => {
                    const course = courseRuns.find(c => c.id === courseRunId);
                    if (!course) {
                        console.error('Course not found for ID:', courseRunId);
                        return null;
                    }
                    
                    const courseDiscountAmount = getDiscountAmount(course);
                    const courseNettPrice = calculatePrice(course);
                    
                    return {
                        participant_name: formData.participant_name,
                        participant_email: formData.participant_email,
                        registration_type: formData.registration_type,
                        nationality: formData.nationality,
                        nric: formData.nric,
                        dob: formData.dob,
                        birth_age: formData.birth_age,
                        mobile_no: formData.mobile_no,
                        draft_course_run_code: course.id, // course_run.id is text, not integer
                        registration_stage: "pending",
                        sponsor_uen: formData.registration_type === "Company-Sponsored" ? formData.sponsor_uen : null,
                        payment_balance: courseNettPrice,
                        discount_amt: courseDiscountAmount,
                        additional_data: additionalData,
                        full_certification_id: formData.full_certification_id,
                        certification_run_id: formData.certification_run_id
                    };
                });
                
                const registrationDataArray = (await Promise.all(registrationPromises)).filter(Boolean);
                console.log('Registration data array:', registrationDataArray);
                
                const {data: insertData, error: insertError} = await supabase
                    .from('registration')
                    .insert(registrationDataArray)
                    .select("id,participant_email");
                
                console.log('Insert result:', { insertData, insertError });
                data = insertData;
                error = insertError;
            } else {
                // Single course registration (existing logic)
                // Resolve enrollment policy to set correct stage, approval fields and snapshot
                const effectivePolicy = await getEffectivePolicy(
                    supabase,
                    formData.course_type_code,
                    formData.course_run_id
                );
                const isRunConfirmed = !!selectedCourse?.is_course_run_confirmed;
                const resolvedStage = getNextRegistrationStage(effectivePolicy, isRunConfirmed);

                // When resubmitting a rejected registration, set stage to 'amended'
                // so admin can review the changes before it proceeds through the flow.
                const submissionStage = existingRegistration?.registration_stage === 'rejected'
                    ? 'amended'
                    : resolvedStage;

                registrationData = {
                    participant_name: formData.participant_name,
                    participant_email: formData.participant_email,
                    registration_type: formData.registration_type,
                    nationality: formData.nationality,
                    nric: formData.nric,
                    dob: formData.dob,
                    birth_age: formData.birth_age,
                    mobile_no: formData.mobile_no,
                    draft_course_run_code: selectedCourse ? selectedCourse.id : null,
                    registration_stage: submissionStage,
                    approval_required: effectivePolicy.needs_approval || false,
                    approval_status: effectivePolicy.needs_approval ? 'pending' : null,
                    policy_snapshot: effectivePolicy,
                    sponsor_uen: formData.registration_type === "Company-Sponsored" ? formData.sponsor_uen : null,
                    payment_balance: (effectivePolicy.commitment_type === 'interest_registration' || effectivePolicy.commitment_type === 'none') ? 0 : nettPrice,
                    discount_amt: discountAmount,
                    additional_data: additionalData
                };
                
                console.log('Attempting to register with data:', registrationData);

                if (registrationId) {
                    // Draft was created for email verification — UPDATE it with full data
                    const {data: updateData, error: updateError} = await supabase
                        .from('registration')
                        .update(registrationData)
                        .eq('id', registrationId)
                        .select("id,participant_email")
                        .single();
                    data = updateData;
                    error = updateError;
                } else {
                    const {data: insertData, error: insertError} = await supabase
                        .from('registration')
                        .insert([registrationData])
                        .select("id,participant_email")
                        .single();
                    data = insertData;
                    error = insertError;
                }
            }

            if (error) {
                console.error('Registration error:', error);
                setIsSubmitting(false);
                alert(`Failed to register. Error: ${error.message}. Please try again.`);
                trackGAEvent('Registration', 'submission_failed', error.message);
            } else {
                // Handle success for both single and multiple registrations
                const isMultipleRegistrations = Array.isArray(data);
                const firstRegistration = isMultipleRegistrations ? data[0] : data;
                
                // Set registration ID for file uploads (use first registration for full certs)
                setRegistrationId(firstRegistration.id);

                // ── Create invoices upfront (source of truth for payment gating) ──
                if (!isMultipleRegistrations && registrationData?.policy_snapshot) {
                    const snap = registrationData.policy_snapshot;
                    const ct = snap.commitment_type || 'full';

                    if (ct !== 'interest_registration' && ct !== 'none') {
                        try {
                            const gstEnabled = orgGstSettings?.gst_enabled || false;
                            const gstRateVal = gstEnabled ? (orgGstSettings.gst_rate || 0) : 0;

                            const { data: existingInvs } = await supabase
                                .from('invoices')
                                .select('id, invoice_type')
                                .eq('registration_id', firstRegistration.id);

                            const existingTypes = new Set((existingInvs || []).map(i => i.invoice_type));

                            if (ct === 'registration_fee' && !existingTypes.has('registration fee')) {
                                const feeAmount = Number(snap.registration_fee_amount || 0);
                                if (feeAmount > 0) {
                                    const { error: regFeeErr } = await supabase.rpc('ensure_registration_fee_invoice', {
                                        p_registration_id: firstRegistration.id,
                                    });
                                    if (regFeeErr) {
                                        console.error('[Registration] ensure_registration_fee_invoice error:', regFeeErr);
                                    } else {
                                        console.log('[Registration] Registration fee invoice ensured via RPC');
                                    }
                                }
                            }

                            if (!existingTypes.has('course') && nettPrice > 0) {
                                const courseGst = gstEnabled
                                    ? Math.round(nettPrice * (gstRateVal / 100) * 100) / 100
                                    : null;
                                const { data: courseInv } = await supabase.from('invoices').insert({
                                    registration_id: firstRegistration.id,
                                    invoice_type: 'course',
                                    status: 'pending payment',
                                    amount_paid: 0,
                                    ...invoiceDueAmount(nettPrice),
                                    invoice_date: new Date().toISOString().split('T')[0],
                                    gst_rate: gstEnabled ? gstRateVal : null,
                                    gst_amount: courseGst,
                                }).select('id').single();
                                if (courseInv) {
                                    const courseName = selectedCourse?.course_type?.name
                                        || formData.course_type_code || 'Course';
                                    await supabase.from('invoice_line_items').insert({
                                        invoice_id: courseInv.id,
                                        description: `Course Fee – ${courseName}`,
                                        quantity: 1,
                                        unit_price: nettPrice,
                                    });
                                    console.log(`[Registration] Course fee invoice created: ${courseInv.id}`);
                                }
                            }
                        } catch (invoiceErr) {
                            console.error('[Registration] Error creating invoices:', invoiceErr);
                        }
                    }
                }

                // Upload any staged files to Supabase Storage and patch additional_data
                const stagedFieldKeys = Object.keys(selectedFiles).filter(
                    key => selectedFiles[key] && selectedFiles[key].length > 0
                );
                if (stagedFieldKeys.length > 0) {
                    console.log('[Registration] Uploading staged files for fields:', stagedFieldKeys);
                    const sanitizedName = (formData.participant_name || 'unknown')
                        .replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
                    const updatedAdditionalData = { ...additionalData };

                    // Determine the correct storage folder path
                    const uploadParams = new URLSearchParams(location.search);
                    const uploadFullCertId = formData.full_certification_id || uploadParams.get('full_certification');
                    const uploadCertRunId = formData.certification_run_id || uploadParams.get('certification_run_id');
                    const folderPath = (uploadFullCertId && uploadCertRunId)
                        ? `registrations/full__cert/${uploadFullCertId}/${uploadCertRunId}/${sanitizedName}`
                        : `registrations/${firstRegistration.id}`;

                    for (const fieldKey of stagedFieldKeys) {
                        const files = Array.from(selectedFiles[fieldKey]);
                        const uploadedPaths = [];
                        const existingVal = getFieldValue(formData, fieldKey);
                        const existingCount = Array.isArray(existingVal) ? existingVal.length : (existingVal ? 1 : 0);

                        for (let i = 0; i < files.length; i++) {
                            const file = files[i];
                            const ext = file.name.split('.').pop() || '';
                            const ts = new Date().toISOString().split('T')[0];
                            const ident = fieldKey.replace(/\./g, '_');
                            const fileName = `${sanitizedName}_${ident}_${ts}_${existingCount + i + 1}.${ext}`;
                            const filePath = `${folderPath}/${fileName}`;

                            const { error: uploadErr } = await supabase.storage
                                .from('Registration_data')
                                .upload(filePath, file, { cacheControl: '3600', upsert: true });

                            if (uploadErr) {
                                console.error(`[Registration] Upload failed for ${file.name}:`, uploadErr.message);
                            } else {
                                uploadedPaths.push(filePath);
                            }
                        }

                        if (uploadedPaths.length > 0) {
                            const keys = fieldKey.split('.');
                            if (keys.length > 1) {
                                let cur = updatedAdditionalData;
                                for (let k = 0; k < keys.length - 1; k++) {
                                    if (!cur[keys[k]]) cur[keys[k]] = {};
                                    cur = cur[keys[k]];
                                }
                                const prev = cur[keys[keys.length - 1]];
                                cur[keys[keys.length - 1]] = Array.isArray(prev)
                                    ? [...prev, ...uploadedPaths]
                                    : (uploadedPaths.length === 1 ? uploadedPaths[0] : uploadedPaths);
                            } else {
                                const prev = updatedAdditionalData[fieldKey];
                                updatedAdditionalData[fieldKey] = Array.isArray(prev)
                                    ? [...prev, ...uploadedPaths]
                                    : (uploadedPaths.length === 1 ? uploadedPaths[0] : uploadedPaths);
                            }
                        }
                    }

                    // Patch the registration(s) with updated additional_data
                    if (isMultipleRegistrations) {
                        for (const reg of data) {
                            await supabase.from('registration')
                                .update({ additional_data: updatedAdditionalData })
                                .eq('id', reg.id);
                        }
                    } else {
                        await supabase.from('registration')
                            .update({ additional_data: updatedAdditionalData })
                            .eq('id', firstRegistration.id);
                    }
                    console.log('[Registration] Staged files uploaded and additional_data patched');
                }

                // If Pay Now is selected, show PayNow QR Modal or Page
                // if (formData.payment_option === 'pay_now') {
                //     setRegistrationId(firstRegistration.id);
                //     setShowPayNowModal(true);
                //     setIsSubmitting(false);
                //     return; // Stop here, showing the modal
                // }

                // Check if course run is confirmed
                const courseToCheck = isMultipleRegistrations ? 
                    courseRuns.find(c => c.id === selectedCourseRunIds[0]) : 
                    selectedCourse;
                
                // Flag: this is a resubmission of a rejected registration (amendment)
                const isAmendment = existingRegistration?.registration_stage === 'rejected';

                if (courseToCheck?.is_course_run_confirmed) {
                    // Course is confirmed - proceed with normal flow
                    if (isMultipleRegistrations) {
                        // For full certifications, don't send emails immediately - wait for signature
                        console.log('Full certification registrations created, emails will be sent after signature collection');
                    } else if (
                        !isAmendment &&
                        (!registrationData?.policy_snapshot?.needs_approval ||
                            registrationData?.policy_snapshot?.commitment_type === 'registration_fee')
                    ) {
                        // Single confirmed course — send registration received email via template
                        // (skip when admin approval is required before payment — pending-approval email is sent from RegistrationPending instead)
                        // Exception: commitment_type registration_fee + needs_approval → student pays first; still send pay/resume link here.
                        // (skip for amendments — admin will communicate after reviewing the changes)
                        // Fire-and-forget — do not block navigation
                        queueRegistrationReceivedEmail(firstRegistration.id);
                    }
                    setIsSubmitting(false);
                    trackGAEvent('Registration', 'submission_successful', 'completed');
                    trackFBEvent('track', 'CompleteRegistration', {
                        content_name: formData.course_type_code,
                        status: true
                    });

                    // Hard reload for amendments so RegistrationHub re-fetches the updated stage.
                    // (React Router won't re-mount if the URL is the same page we're already on.)
                    if (isAmendment) {
                        window.location.href = `/register/${firstRegistration.id}`;
                    } else {
                        navigate(`/register/${firstRegistration.id}`);
                    }
                } else {
                    // Course is not confirmed — registration sits at interest_received
                    if (isMultipleRegistrations) {
                        console.log('Full certification course interest registrations created, emails will be sent after signature collection');
                    } else if (!isAmendment) {
                        // Send "Awaiting Course Confirmation" email to participant
                        // (skip for amendments — admin reviews first)
                        try {
                            if (shouldSkipSessionEdgeInvoke('awaiting_course_confirmation', firstRegistration.id)) {
                                console.warn('[Registration] Skipping duplicate send-awaiting-confirmation-email invoke (same tab session)');
                            } else {
                                const { error: awaitEmailError } = await supabase.functions.invoke(
                                    'send-awaiting-confirmation-email',
                                    { body: { registrationId: firstRegistration.id } }
                                );
                                if (awaitEmailError) {
                                    console.error('❌ send-awaiting-confirmation-email error:', awaitEmailError);
                                } else {
                                    rememberSessionEdgeInvoke('awaiting_course_confirmation', firstRegistration.id);
                                    console.log('✅ Awaiting course confirmation email sent');
                                }
                            }
                        } catch (emailErr) {
                            console.error('❌ send-awaiting-confirmation-email threw:', emailErr);
                        }
                    }

                    setIsSubmitting(false);
                    trackGAEvent('Registration', 'submission_successful', 'course_interest');
                    trackFBEvent('track', 'CompleteRegistration', {
                        content_name: formData.course_type_code,
                        status: 'course_interest'
                    });
                    if (isAmendment) {
                        window.location.href = `/register/${firstRegistration.id}`;
                    } else {
                        navigate(`/register/${firstRegistration.id}`);
                    }
                }
            }
        } catch (error) {
            console.error('Unexpected error during registration:', error);
            setIsSubmitting(false);
            alert(`Registration failed due to an unexpected error: ${error.message}. Please try again.`);
            trackGAEvent('Registration', 'submission_failed', 'unexpected_error');
        }
    };


    return (
        <Box>
            {(!selectedCourse?.ssg_course || registration_type === "Self-Sponsored" || registration_type === "Company-Sponsored" || formData.registration_type) ? (
                <Box>
                    {(() => {
                        const selectedCourse = courseRuns.find(course => course.id === formData.course_run_id);
                        // Always show the submit button, even if no course is selected yet
                        
                        return (
                            <>
                                {selectedCourse && !selectedCourse?.is_course_run_confirmed && gateOnRunConfirmed && (
                                    <Alert status="warning" mb={4} borderRadius="md">
                                        <AlertIcon />
                                        <Text fontSize="sm">
                                            <strong>Note:</strong> This course run is pending confirmation. 
                                            Your registration will be recorded as course interest, and we'll notify you via email once the course is confirmed.
                                        </Text>
                                    </Alert>
                                )}

                                {/* Confirmation Fee Selection */}
                                {/* <FormControl mt={8} p={6} borderWidth="1px" borderRadius="lg" borderColor="brand.300" bg="white">
                                    <FormLabel fontWeight="bold" color="brand.700" fontSize="lg" mb={4}>
                                        Secure your seat
                                    </FormLabel>
                                    <RadioGroup 
                                        onChange={(val) => setFormData(prev => ({ ...prev, payment_option: val }))} 
                                        value={formData.payment_option || 'pay_now'}
                                    >
                                        <Stack spacing={4}>
                                            <Box 
                                                p={4} 
                                                borderWidth="1px" 
                                                borderRadius="md" 
                                                cursor="pointer"
                                                borderColor={formData.payment_option === 'pay_now' ? "brand.500" : "gray.200"}
                                                bg={formData.payment_option === 'pay_now' ? "brand.50" : "transparent"}
                                                onClick={() => setFormData(prev => ({ ...prev, payment_option: 'pay_now' }))}
                                            >
                                                <Radio value="pay_now" colorScheme="brand">
                                                    <Text fontWeight="bold">Pay registration fee now (S$100.00)</Text>
                                                    <Text fontSize="sm" color="gray.600">Secure your seat immediately via PayNow QR code.</Text>
                                                </Radio>
                                            </Box>
                                            <Box 
                                                p={4} 
                                                borderWidth="1px" 
                                                borderRadius="md" 
                                                cursor="pointer"
                                                borderColor={formData.payment_option === 'pay_later' ? "brand.500" : "gray.200"}
                                                bg={formData.payment_option === 'pay_later' ? "brand.50" : "transparent"}
                                                onClick={() => setFormData(prev => ({ ...prev, payment_option: 'pay_later' }))}
                                            >
                                                <Radio value="pay_later" colorScheme="brand">
                                                    <Text fontWeight="bold">Pay later</Text>
                                                    <Text fontSize="sm" color="gray.600">Submit your registration first. You will receive a payment link via email.</Text>
                                                </Radio>
                                            </Box>
                                        </Stack>
                                    </RadioGroup>
                                </FormControl> */}

                                {formData.course_run_id && (
                                <Box
                                    position="fixed"
                                    bottom={0}
                                    left={0}
                                    right={0}
                                    bg="white"
                                    borderTop="1px solid"
                                    borderColor="gray.200"
                                    py={{base: 3, md: 4}}
                                    px={{base: 4, md: 6}}
                                    zIndex={50}
                                    boxShadow="0 -4px 20px rgba(0,0,0,0.1)"
                                >
                                <Flex
                                    justifyContent="center"
                                    align="center"
                                    gap={3}
                                    direction="column"
                                    maxW="600px"
                                    mx="auto"
                                >
                                    {needsEmailVerification && !emailVerified && (
                                        <Button
                                            width="100%"
                                            size="lg"
                                            color="white"
                                            bg={emailVerificationSent ? "brand.500" : "brand.600"}
                                            _hover={{ bg: "brand.700" }}
                                            isLoading={isSendingVerification}
                                            loadingText="Sending..."
                                            isDisabled={!formData.participant_email || !formData.course_run_id || !onSendVerificationEmail}
                                            onClick={onSendVerificationEmail}
                                            borderRadius="xl"
                                            boxShadow="lg"
                                        >
                                            {emailVerificationSent ? "Resend Verification Email" : "Verify Email"}
                                        </Button>
                                    )}
                                    {(!needsEmailVerification || emailVerified) && (
                                        <Button
                                            isLoading={isSubmitting}
                                            isDisabled={
                                                lxCopilotDirectoryIntake &&
                                                (directoryListingStatus !== "valid" || !lxDirectoryFormReady)
                                            }
                                            size="lg"
                                            width="100%"
                                            colorScheme="brand"
                                            height={{base: "54px", md: "50px"}}
                                            fontSize={{base: "md", md: "md"}}
                                            boxShadow="lg"
                                            borderRadius="xl"
                                            onClick={() => {
                                                // Check for missing required fields
                                                const missingFields = [];
                                                
                                                // Check basic required fields
                                                const params = new URLSearchParams(location.search);
                                                const fullCertification = params.get('full_certification');
                                                
                                                if (fullCertification) {
                                                    // For full certifications, check if modules are selected
                                                    console.log('Full certification validation:', {
                                                        selectedCourseRunIds: selectedCourseRunIds,
                                                        length: selectedCourseRunIds.length
                                                    });
                                                    if (selectedCourseRunIds.length === 0) missingFields.push("Course Selection");
                                                } else {
                                                    // For single courses, check course_run_id
                                                    console.log('Single course validation:', {
                                                        course_run_id: formData.course_run_id
                                                    });
                                                    if (!formData.course_run_id) missingFields.push("Course Selection");
                                                }
                                                const fieldInTemplate = (fieldKey) => {
                                                    if (stepTemplateFieldIds === null) return true;
                                                    const config = additionalFields.find(f => f.field_key === fieldKey && f.is_original_field);
                                                    return !config || stepTemplateFieldIds.includes(config.id);
                                                };
                                                if (fieldInTemplate('nationality') && !formData.nationality) missingFields.push("Nationality");
                                                if (fieldInTemplate('dob') && !formData.birth_age) missingFields.push("Age");
                                                if (fieldInTemplate('participant_name') && !formData.participant_name) missingFields.push("Participant Name");
                                                if (fieldInTemplate('participant_email') && !formData.participant_email) missingFields.push("Email Address");
                                                if (fieldInTemplate('mobile_no') && !formData.mobile_no) missingFields.push("Mobile Number");
                                                
                                                // Check validation errors
                                                if (fieldInTemplate('participant_email') && validationErrors.email) missingFields.push("Valid Email Address");
                                                if (fieldInTemplate('mobile_no') && validationErrors.mobile) missingFields.push("Valid Mobile Number");
                                                if (fieldInTemplate('dob') && validationErrors.dob) missingFields.push("Valid Date of Birth");
                                                
                                                // Check company-sponsored specific fields
                                                if (formData.registration_type === "Company-Sponsored") {
                                                    if (validationErrors.sponsor_uen || !formData.sponsor_uen) missingFields.push("Sponsor UEN");
                                                    if (validationErrors.isSME) missingFields.push("SME Status");
                                                    if (validationErrors.isTeamRegistration) missingFields.push("Team Registration Status");
                                                }
                                                
                                                // Check NRIC for Singapore citizens/PRs (only if NRIC field is in template)
                                                if (fieldInTemplate('nric') &&
                                                    (formData.nationality === "Singapore Citizen" || formData.nationality === "Singapore Permanent Resident") && 
                                                    (validationErrors.nric || !formData.nric)) {
                                                    missingFields.push("Valid NRIC");
                                                }
                                                
                                                // Check additional field validation errors (exclude signature fields)
                                                const additionalFieldErrors = Object.keys(validationErrors).filter(key => 
                                                    key.includes('.') && 
                                                    validationErrors[key] !== false &&
                                                    !key.includes('signature')
                                                );
                                                if (additionalFieldErrors.length > 0) {
                                                    missingFields.push("Valid Additional Information");
                                                }
                                                
                                                // Check required additional fields (exclude auto-filled fields, include staged files)
                                                if (!checkRequiredAdditionalFields()) {
                                                    const requiredAdditionalFields = additionalFields.filter(field => {
                                                        const isRequired = getEffectiveRequiredStatus(field);
                                                        return isRequired && 
                                                            field.field_key !== 'legal.declaration_date' && // Exclude auto-filled declaration date
                                                            !field.field_key.includes('signature'); // Exclude all signature fields
                                                    });
                                                    const missingAdditionalFields = requiredAdditionalFields.filter(field => {
                                                        const fieldValue = getFieldValue(formData, field.field_key);
                                                        const stagedFiles = selectedFiles[field.field_key];
                                                        const hasValue = (fieldValue && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0));
                                                        const hasStagedFiles = (stagedFiles && stagedFiles.length > 0);
                                                        return !hasValue && !hasStagedFiles;
                                                    });
                                                    
                                                    missingAdditionalFields.forEach(field => {
                                                        missingFields.push(field.field_label);
                                                    });
                                                }
                                                
                                                // If there are missing fields, show toast and return
                                                if (missingFields.length > 0) {
                                                    toast({
                                                        title: "Missing Required Fields",
                                                        description: `Please fill in: ${missingFields.join(', ')}`,
                                                        status: "error",
                                                        duration: 5000,
                                                        isClosable: true,
                                                    });
                                                    return;
                                                }
                                                
                                                // Track form completion before submission
                                                trackFBEvent('trackCustom', 'FormCompletion', {
                                                    fields_complete: true,
                                                    registration_type: formData.registration_type,
                                                    nationality: formData.nationality,
                                                    age_group: parseInt(formData.birth_age) >= 40 ? "40+" : "Below 40"
                                                });
                                                // Proceed with registration
                                                insertRegistration();
                                            }}
                                        >
                                            {existingRegistration?.registration_stage === 'rejected'
                                                ? 'Submit Amendment'
                                                : 'Submit Registration'}
                                        </Button>
                                    )}
                                </Flex>
                                </Box>
                                )}
                            </>
                        );
                    })()}
                </Box>
            ) : null}

            {/* PayNow QR Modal */}
            <Modal isOpen={showPayNowModal} onClose={() => setShowPayNowModal(false)} size="lg" closeOnOverlayClick={false}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader textAlign="center">Secure Your Seat</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={6} py={4}>
                            <VStack spacing={2}>
                                <Text textAlign="center" fontSize="sm" color="gray.600">
                                    To secure your seat immediately, please pay the:
                                </Text>
                                <Text fontSize="md" fontWeight="bold" color="brand.600" textTransform="uppercase" letterSpacing="wider">Registration Fee</Text>
                                <Heading size="xl" color="brand.700">S$ 100.00</Heading>
                            </VStack>
                            
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                                <VStack 
                                    p={4} 
                                    borderWidth="1px" 
                                    borderRadius="lg" 
                                    bg="green.50" 
                                    borderColor="green.200"
                                    align="center"
                                    spacing={3}
                                >
                                    <Text fontWeight="bold" color="green.700" fontSize="sm">Option 1: PayNow</Text>
                                    <VStack spacing={2} align="center" w="100%">
                                        <Box bg="white" p={2} borderRadius="md" boxShadow="sm">
                                            <img 
                                                src="/paynow-qr.png" 
                                                alt="PayNow QR Code" 
                                                style={{ width: '150px', height: 'auto' }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = "https://via.placeholder.com/150x150?text=PayNow+QR";
                                                }}
                                            />
                                        </Box>
                                        <Button 
                                            size="xs" 
                                            variant="outline" 
                                            colorScheme="green"
                                            onClick={() => {
                                                const link = document.createElement('a');
                                                link.href = '/paynow-qr.png';
                                                link.download = `PayNow-QR-${registrationId.substring(0, 8)}.png`;
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                            }}
                                        >
                                            Save QR to Gallery
                                        </Button>
                                    </VStack>
                                    <VStack spacing={1} align="center">
                                        <Text fontSize="xs" fontWeight="bold">Ref: {registrationId?.substring(0, 8).toUpperCase()}</Text>
                                        <Text fontSize="10px" textAlign="center" color="gray.600">Scan or upload QR in bank app, then click verify:</Text>
                                    </VStack>
                                    <Button 
                                        size="sm" 
                                        colorScheme="green" 
                                        w="100%"
                                        onClick={handlePayNowDone}
                                        isLoading={isUpdatingStatus}
                                    >
                                        I have paid via PayNow
                                    </Button>
                                </VStack>

                                <VStack 
                                    p={4} 
                                    borderWidth="1px" 
                                    borderRadius="lg" 
                                    bg="blue.50" 
                                    borderColor="blue.200"
                                    align="center"
                                    spacing={3}
                                    justify="center"
                                >
                                    <Text fontWeight="bold" color="blue.700" fontSize="sm">Option 2: Credit Card</Text>
                                    <Icon as={HiCreditCard} boxSize={12} color="blue.500" />
                                    <VStack spacing={0}>
                                        <Text fontSize="10px" textAlign="center" color="gray.600">Pay securely via Stripe</Text>
                                        <Text fontSize="10px" textAlign="center" color="orange.600" fontWeight="bold">(Service charge applicable)</Text>
                                    </VStack>
                                    <Button 
                                        size="sm" 
                                        colorScheme="blue" 
                                        w="100%"
                                        onClick={handleStripePayment}
                                        isLoading={isProcessingPayment}
                                    >
                                        Pay via Stripe
                                    </Button>
                                </VStack>
                            </SimpleGrid>

                            <Alert status="info" borderRadius="md" size="sm">
                                <AlertIcon />
                                <Text fontSize="xs">
                                    Your registration is being processed. After payment, we will verify and send your LMS login details shortly.
                                </Text>
                            </Alert>
                        </VStack>
                    </ModalBody>
                    <ModalFooter bg="gray.50" borderBottomRadius="md">
                        <Button variant="ghost" mr={3} onClick={() => navigate(`/register/${registrationId}`)}>
                            I'll pay later
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    )
}

const Checkout = (props) => {
    const {formData, setFormData, courseRuns, allowedTab, setTabIndex} = props

    return (
        <Box>
            <Spinner/>
            <Text>Please wait as we redirect you to checkout</Text>
        </Box>
    )
}

const Step3 = (props) => {
    const {formData, setFormData, courseRuns, allowedTab, setTabIndex} = props
    const [submitting, setSubmitting] = useState(false)
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const course_run_id = params.get("course_run_id")
    const selected_course_run = courseRuns.filter((each) => each.attendance_id === course_run_id)[0]
    const toast = useToast()

    const insertRegistration = async () => {
        setSubmitting(true);

        try {
            const {data, error} = await supabase
                .from('registration')
                .insert([
                    {
                        participant_name: formData?.participant_name,
                        participant_email: formData?.participant_email,
                        billing_type: formData?.billing_type,
                        registration_type: formData?.registration_type,
                        course_run_id: selected_course_run.id,
                        registration_stage: "pending",
                    },
                ])
                .select("id, participant_email")
                .single();

            if (error) throw error;

            return data;
        } catch (error) {
            console.error('Error inserting registration:', error);
            setSubmitting(false);
            throw error; // Re-throw error so it can be caught later
        }
    };

    const handleCheckout = async (registration) => {
        try {
            const response = await fetch('https://eovhmnu1acpextc.m.pipedream.net', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    product_id: selected_course_run.course_type_code,
                    registration_id: registration.id,
                    participant_email: registration.participant_email
                }),
            });

            const data = await response.json();

            if (data.url) {
                window.location.href = data.url;
                setSubmitting(false);
            } else {
                console.error('No URL found in response');
            }
        } catch (error) {
            console.error('Error during the checkout process:', error);
        }
    };


    return (
        <div>
            <Alert status='info' mb={8}>
                <AlertIcon/>
                Please view the terms of the Student Contract carefully before proceeding to checkout
            </Alert>
            <Text fontSize={"lg"} as={"b"}>STUDENT CONTRACT (FOR SELF-SPONSORED)</Text>
            <Divider size={"md"} colorScheme={"blackAlpha"} mb={4}/>
            This contract binds the Training Provider and the Student once both parties sign this Contract.
            This contract is made between:
            <br/>
            <br/>
            <ol type="a" style={{marginLeft: "20px", listStyleType: "lower-alpha"}}>
                <li>
                    <HStack mb={1}>
                        <Text color="fg.muted" fontWeight={300} mr={3}>
                            Registered Name of Training Provider:
                        </Text>
                        <Text>SOZCODE PTE. LTD</Text>
                        Registration Number: 202139596G<br/>
                    </HStack>
                    <HStack mb={1}>
                        <Text color="fg.muted" fontWeight={300} mr={3}>
                            Registration Number:
                        </Text>
                        <Text>202139596G</Text>
                    </HStack>
                </li>
                <br/>
                <li>
                    <HStack mb={1}>
                        <Text color="fg.muted" fontWeight={300} mr={3}>
                            Student Name:
                        </Text>
                        <Text>{formData?.participant_name}</Text>
                    </HStack>
                    <HStack mb={1}>
                        <Text color="fg.muted" fontWeight={300} mr={3}>
                            Email:
                        </Text>
                        <Text>{formData?.participant_email}</Text>
                    </HStack>

                </li>
            </ol>
            <br/>
            <br/>
            <strong>Course Information</strong>
            <Divider size={"md"} colorScheme={"blackAlpha"} mb={4}/>
            <HStack mb={1}>
                <Text color="fg.muted" fontWeight={300} mr={3}>
                    Course Title:
                </Text>
                <Text>{selected_course_run?.name}</Text>
            </HStack>
            <HStack mb={1}>
                <Text color="fg.muted" fontWeight={300} mr={3}>
                    Course Commencement Date:
                </Text>
                <Text>{moment(selected_course_run?.start_date).format("DD MMM YYYY")}</Text>
            </HStack>
            <HStack mb={1}>
                <Text color="fg.muted" fontWeight={300} mr={3}>
                    Course End Date:
                </Text>
                <Text>{moment(selected_course_run?.end_date).format("DD MMM YYYY")}</Text>
            </HStack>
            <br/>
            <br/>
            <strong>Course Fees</strong>
            <Divider size={"md"} colorScheme={"blackAlpha"} mb={4}/>
            <table style={{border: "1px solid black", width: "100%"}}>
                <thead>
                <tr>
                    <th style={{border: "1px solid black"}}>
                        <td>Fee Breakdown</td>
                    </th>
                    <th style={{border: "1px solid black"}}>
                        <td>Amount</td>
                    </th>
                </tr>
                </thead>

                <tbody>
                <tr style={{border: "1px solid black"}}>
                    <td style={{border: "1px solid black"}}>
                        Full Course Fee
                    </td>
                    <td style={{border: "1px solid black"}}>
                        SGD {selected_course_run?.price}
                    </td>
                </tr>
                <tr>
                    <td style={{border: "1px solid black"}}>
                        Nett fee payable by individual
                    </td>
                    <td style={{border: "1px solid black"}}>
                        SGD {selected_course_run?.price}
                    </td>
                </tr>
                </tbody>
            </table>
            <br/>
            <br/>
            <strong>Refund/Withdrawal and Rescheduling Policy</strong>
            <Divider size={"md"} colorScheme={"blackAlpha"} mb={4}/>
            <table style={{border: "1px solid black", width: "100%"}}>
                <thead>


                <tr>
                    <th style={{border: "1px solid black"}}>
                        <td>Request for Withdrawal</td>
                    </th>
                    <th style={{border: "1px solid black"}}>
                        <td>Options for student</td>
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr style={{border: "1px solid black"}}>
                    <td style={{border: "1px solid black"}}>
                        Withdrawal request made before sending of course confirmation email
                    </td>
                    <td style={{border: "1px solid black"}}>
                        · Cancel with no cancellation charges
                    </td>
                </tr>
                <tr>
                    <td style={{border: "1px solid black"}}>
                        Withdrawal request made at least 10 working days before the course start date
                    </td>
                    <td style={{border: "1px solid black"}}>
                        · Re-scheduling for other course runs (if available)<br/>
                        · Send someone else in your place<br/>
                        · Cancel with 100% course fees still payable<br/>
                    </td>
                </tr>
                <tr>
                    <td style={{border: "1px solid black"}}>
                        Withdrawal request made less than 10 working days before the course start date
                    </td>
                    <td style={{border: "1px solid black"}}>
                        · Send someone else in your place<br/>
                        · Cancel with 100% course fees still payable<br/>
                    </td>
                </tr>
                </tbody>
            </table>
            <br/>
            <br/>

            <ol type="a" style={{marginLeft: "20px", listStyleType: "lower-alpha"}}>
                <li>The Training Provider reserves the right to change or cancel the course due to unforeseen
                    circumstances. A full refund of any course fees paid will be made to the Student in this
                    circumstance.

                </li>
                <br/>
                <li>A withdrawal request can be made via sending an email to courses@sozcode.com.

                </li>
                <br/>
            </ol>
            <br/>
            <br/>
            <FormLabel> I hereby acknowledge and agree to the terms stated in this Contract.
            </FormLabel>
            <Box>
                <Button mt="4" mb="8" size={"lg"} isLoading={submitting} onClick={insertRegistration}>
                    Proceed to Payment
                </Button>
            </Box>
        </div>
    )
}

export const Registration = ({ existingRegistration } = {}) => {
    const [courseRuns, setCourseRuns] = useState([]);
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false)
    const [success, setSuccess] = useState(null)
    const [loadingCourseRuns, setLoadingCourseRuns] = useState(false)
    const {courseRunId} = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();
    const [formData, setFormData] = useState({
        participant_name: '',
        participant_email: '',
        registration_type: '',
        course_run_id: '',
        course_type_code: '',
        full_certification_id: '',
        certification_run_id: '',
        diploma_group: '',
        dob: '',
        displayDob: '',
        // ... other fields
    });
    const [isTeamRegistration, setIsTeamRegistration] = useState(null);
    const [isSME, setIsSME] = useState(null);
    const [isUENValid, setIsUENValid] = useState(false);
    const [registrationId, setRegistrationId] = useState(null);
    const [additionalFields, setAdditionalFields] = useState([]);
    const [basicFieldsOrder, setBasicFieldsOrder] = useState([]);
    const [compulsoryCustomFields, setCompulsoryCustomFields] = useState([]);
    const [templateFieldIds, setTemplateFieldIds] = useState(null);
    const [emailVerified, setEmailVerified] = useState(false);
    const [emailVerificationSent, setEmailVerificationSent] = useState(false);
    const [isSendingVerification, setIsSendingVerification] = useState(false);
    const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
    const [gateOnRunConfirmed, setGateOnRunConfirmed] = useState(false);
    const [selectedCourseRunIds, setSelectedCourseRunIds] = useState([]);
    const [fullCertificationCost, setFullCertificationCost] = useState(null);
    const [orgGstSettings, setOrgGstSettings] = useState({ gst_enabled: false, gst_rate: 9 });

    /** LX Copilot + Edusphere directory: `?directory_channel=...&referrer=lxcopilot` */
    const directoryChannelParam = useMemo(
        () => new URLSearchParams(location.search).get("directory_channel") || "",
        [location.search],
    );
    const lxCopilotDirectoryIntake = useMemo(() => {
        if (existingRegistration) return false;
        const ref = new URLSearchParams(location.search).get("referrer");
        return !!(directoryChannelParam && ref?.toLowerCase() === "lxcopilot");
    }, [directoryChannelParam, location.search, existingRegistration]);

    /** idle = not applicable; checking; valid; invalid */
    const [directoryListingStatus, setDirectoryListingStatus] = useState("idle");
    const [directoryIntakeSubmitted, setDirectoryIntakeSubmitted] = useState(false);
    
    // File upload state for compulsory fields
    const [selectedFiles, setSelectedFiles] = useState({});
    const [uploadingFiles, setUploadingFiles] = useState(new Set());

    const [tabIndex, setTabIndex] = useState(0)
    const [allowedTab, setAllowedTab] = useState(0)

    const handleTabsChange = (index) => {
        setTabIndex(index)
    }

    // Fetch additional fields for validation
    useEffect(() => {
        const fetchAdditionalFields = async () => {
            try {
                const { data, error } = await supabase
                    .from('additional_registration_fields')
                    .select('id, field_key, is_required, field_label, conditional_requirements, field_type, is_original_field')
                    .or('field_status.is.null,field_status.eq.active');
                
                if (error) throw error;
                const normalized = (data || []).map((row) => ({
                    ...row,
                    conditional_requirements: normalizeConditionalRequirements(row.conditional_requirements),
                }));
                setAdditionalFields(normalized);
            } catch (err) {
                console.error('Error fetching additional fields:', err);
            }
        };
        
        fetchAdditionalFields();
    }, []);

    // Pre-fill form when editing a rejected registration (amendment flow)
    useEffect(() => {
        if (!existingRegistration) return;
        const r = existingRegistration;

        // Spread additional_data into formData (excluding internal metadata keys)
        const { rejection_history: _rh, ...additionalSpread } = r.additional_data || {};

        // type="date" inputs expect YYYY-MM-DD natively
        const displayDob = r.dob || '';

        setFormData(prev => ({
            ...prev,
            participant_name:  r.participant_name  || '',
            participant_email: r.participant_email || '',
            mobile_no:         r.mobile_no         || '',
            nric:              r.nric              || '',
            dob:               r.dob               || '',
            displayDob,
            nationality:       r.nationality       || '',
            registration_type: r.registration_type || '',
            birth_age:         r.birth_age         || '',
            sponsor_uen:       r.sponsor_uen       || '',
            course_run_id:     r.draft_course_run_code || '',
            course_type_code:  r.course_type_code  || '',
            // Restore additional field values from previous submission
            ...additionalSpread,
        }));

        // Anchor the submission to the existing row (UPDATE instead of INSERT)
        setRegistrationId(r.id);
        // Email was already verified on the original submission — skip re-verification
        setEmailVerified(true);
        // Allow all tabs so the participant can review/edit any section
        setAllowedTab(99);
    }, [existingRegistration]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve registration template when the selected course changes.
    // Priority: course_run.registration_template_id → course_type.registration_template_id → all fields
    useEffect(() => {
        const resolveTemplate = async () => {
            if (!formData.course_run_id || !courseRuns.length) return;

            const selectedCourse = courseRuns.find(c => c.id === formData.course_run_id);
            const templateId =
                selectedCourse?.registration_template_id ||          // run-level (highest priority)
                selectedCourse?.course_type?.registration_template_id; // type-level fallback

            if (!templateId) {
                setTemplateFieldIds(null); // No template → show all fields
                return;
            }

            try {
                const { data, error } = await supabase
                    .from('registration_templates')
                    .select('field_ids')
                    .eq('id', templateId)
                    .single();

                if (error) throw error;

                // field_ids is [{field_id, display_order}, …]
                const ids = (data?.field_ids || []).map(f => f.field_id);
                setTemplateFieldIds(ids);
            } catch (err) {
                console.error('Error fetching registration template:', err);
                setTemplateFieldIds(null);
            }
        };

        resolveTemplate();
    }, [formData.course_run_id, courseRuns]);

    // Send email verification link (mirrors FullCertRegistration pattern)
    const sendVerificationEmail = async (regId, participantEmail, participantName) => {
        if (!regId || !participantEmail) return false;
        setIsSendingVerification(true);
        try {
            const { data, error } = await supabase.functions.invoke('send-verification-email', {
                body: {
                    registrationId: regId,
                    participantEmail,
                    participantName: participantName || formData.participant_name || 'Participant',
                    baseUrl: getAppOrigin(),
                },
            });
            if (error) throw error;
            if (data?.success) {
                setEmailVerificationSent(true);
                setEmailVerified(false);
                return true;
            }
            throw new Error(data?.error || 'Failed to send verification email');
        } catch (err) {
            console.error('Error sending verification email:', err);
            return false;
        } finally {
            setIsSendingVerification(false);
        }
    };

    // Send verification email only (creates draft if needed); used when submit button is disabled until verified
    const handleSendVerificationEmail = async () => {
        const params = new URLSearchParams(location.search);
        if (params.get('full_certification')) return;
        const selectedCourse = courseRuns.find(c => c.id === formData.course_run_id);
        if (!formData.participant_email || !selectedCourse) {
            toast({
                title: "Complete required fields",
                description: "Please enter your email and select a course first.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }
        if (registrationId) {
            await sendVerificationEmail(registrationId, formData.participant_email, formData.participant_name);
            toast({
                title: "Verification link sent",
                description: `A new verification link has been sent to ${formData.participant_email}.`,
                status: "info",
                duration: 5000,
                isClosable: true,
            });
            return;
        }
        setIsSendingVerification(true);
        try {
            const { data: draft, error: draftErr } = await supabase
                .from('registration')
                .insert([{
                    participant_name: formData.participant_name,
                    participant_email: formData.participant_email,
                    draft_course_run_code: selectedCourse?.id || null,
                    registration_stage: 'draft',
                }])
                .select('id')
                .single();
            if (draftErr) throw draftErr;
            setRegistrationId(draft.id);
            const sent = await sendVerificationEmail(draft.id, formData.participant_email, formData.participant_name);
            if (sent) {
                toast({
                    title: "Verify your email",
                    description: `A verification link has been sent to ${formData.participant_email}. Click the link, then return here to submit.`,
                    status: "info",
                    duration: 10000,
                    isClosable: true,
                });
            }
        } catch (err) {
            console.error('Error sending verification email:', err);
            toast({
                title: "Error",
                description: err?.message || "Failed to send verification email. Please try again.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSendingVerification(false);
        }
    };

    // Poll for email verification (only while waiting for user to click link)
    useEffect(() => {
        if (!registrationId || emailVerified || !emailVerificationSent) return;

        const checkVerification = async () => {
            try {
                const { data, error } = await fetchPublicRegistration(supabase, registrationId);
                if (!error && data?.email_verified) {
                    setEmailVerified(true);
                    toast({
                        title: "Email Verified!",
                        description: "Your email has been verified. Click Submit to complete your registration.",
                        status: "success",
                        duration: 5000,
                        isClosable: true,
                    });
                }
            } catch (err) {
                console.error('Error checking verification status:', err);
            }
        };

        const interval = setInterval(checkVerification, 5000);
        return () => clearInterval(interval);
    }, [registrationId, emailVerified, emailVerificationSent]);

    // Resolve whether email verification is required (standalone only; full cert has its own flow)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (lxCopilotDirectoryIntake) {
            setNeedsEmailVerification(false);
            setGateOnRunConfirmed(false);
            return;
        }
        if (params.get('full_certification')) {
            setNeedsEmailVerification(false);
            setGateOnRunConfirmed(false);
            return;
        }
        const courseTypeCode = formData.course_type_code;
        const courseRunId = formData.course_run_id;
        if (!courseTypeCode || !courseRunId) {
            setNeedsEmailVerification(false);
            setGateOnRunConfirmed(false);
            return;
        }
        let cancelled = false;
        getEffectivePolicy(supabase, courseTypeCode, courseRunId).then((policy) => {
            if (!cancelled) {
                setNeedsEmailVerification(policy.needs_email_verification === true);
                setGateOnRunConfirmed(policy.gate_on_course_run_confirmed === true);
            }
        }).catch(() => {
            if (!cancelled) {
                setNeedsEmailVerification(false);
                setGateOnRunConfirmed(false);
            }
        });
        return () => { cancelled = true; };
    }, [location.search, formData.course_type_code, formData.course_run_id, lxCopilotDirectoryIntake]);

    // Fetch system (original) field config from the DB.
    // This drives the rendering of the core identity block — field_type, is_required, is_hidden, and
    // maps_to_column let the LMS adapt to each tenant without hardcoding SG-specific behaviour.
    useEffect(() => {
        const fetchBasicFieldsOrder = async () => {
            try {
                const { data, error } = await supabase
                    .from('additional_registration_fields')
                    .select('id, field_key, display_order, field_behaviors, field_options, field_type, is_required, is_hidden, maps_to_column, field_label, validation_rules')
                    .or('field_status.is.null,field_status.eq.active')
                    .eq('is_original_field', true)
                    .order('display_order', { ascending: true });
                
                if (error) throw error;
                setBasicFieldsOrder(data || []);
            } catch (err) {
                console.error('Error fetching basic fields order:', err);
            }
        };
        
        fetchBasicFieldsOrder();
    }, []);

    // Fetch organization GST settings
    useEffect(() => {
        const fetchOrgGstSettings = async () => {
            try {
                const { data, error } = await supabase
                    .from('organization')
                    .select('gst_enabled, gst_rate')
                    .single();
                
                if (error) throw error;
                if (data) {
                    setOrgGstSettings({
                        gst_enabled: data.gst_enabled || false,
                        gst_rate: data.gst_rate || 9
                    });
                }
            } catch (err) {
                console.error('Error fetching organization GST settings:', err);
            }
        };
        
        fetchOrgGstSettings();
    }, []);

    // Fetch full certification cost when we have a full certification
    useEffect(() => {
        const fetchFullCertificationCost = async () => {
            const params = new URLSearchParams(location.search);
            const fullCertificationId = params.get('full_certification');
            
            if (fullCertificationId && !fullCertificationCost) {
                try {
                    const { data, error } = await supabase
                        .from('full_certification')
                        .select('cost')
                        .eq('code', fullCertificationId)
                        .single();
                    
                    if (error) throw error;
                    setFullCertificationCost(parseFloat(data.cost || 0));
                } catch (err) {
                    console.error('Error fetching full certification cost:', err);
                }
            }
        };
        
        fetchFullCertificationCost();
    }, [location.search, fullCertificationCost]);

    // Validate directory_run_listing (TMS RPC) for LX Copilot + directory_channel deep links
    useEffect(() => {
        if (!lxCopilotDirectoryIntake || !directoryChannelParam) {
            setDirectoryListingStatus("idle");
            return;
        }
        let cancelled = false;
        const fullCert = new URLSearchParams(location.search).get("full_certification");

        const runValidation = async () => {
            setDirectoryListingStatus("checking");
            try {
                if (fullCert) {
                    if (!selectedCourseRunIds.length) {
                        if (!cancelled) setDirectoryListingStatus("idle");
                        return;
                    }
                    for (const runId of selectedCourseRunIds) {
                        const r = await validateDirectoryRunListing(supabase, {
                            runKind: "course_run",
                            runCode: runId,
                            directoryChannel: directoryChannelParam,
                        });
                        if (cancelled) return;
                        if (!r.ok) {
                            setDirectoryListingStatus("invalid");
                            return;
                        }
                    }
                    if (!cancelled) setDirectoryListingStatus("valid");
                } else if (formData.course_run_id) {
                    const r = await validateDirectoryRunListing(supabase, {
                        runKind: "course_run",
                        runCode: formData.course_run_id,
                        directoryChannel: directoryChannelParam,
                    });
                    if (cancelled) return;
                    setDirectoryListingStatus(r.ok ? "valid" : "invalid");
                } else {
                    if (!cancelled) setDirectoryListingStatus("idle");
                }
            } catch (e) {
                console.error("directory listing validation:", e);
                if (!cancelled) setDirectoryListingStatus("invalid");
            }
        };

        void runValidation();
        return () => {
            cancelled = true;
        };
    }, [
        lxCopilotDirectoryIntake,
        directoryChannelParam,
        location.search,
        formData.course_run_id,
        selectedCourseRunIds,
    ]);

    // Must be declared before isFieldConditionallyRequired (avoids TDZ: "Cannot access … before initialization")
    const getFieldValue = (formDataArg, fieldKey) => {
        if (formDataArg[fieldKey] !== undefined && formDataArg[fieldKey] !== null && formDataArg[fieldKey] !== '') {
            return formDataArg[fieldKey];
        }
        if (fieldKey.includes('.')) {
            const keys = fieldKey.split('.');
            let current = formDataArg;
            for (const key of keys) {
                if (current && typeof current === 'object' && key in current) {
                    current = current[key];
                } else {
                    return '';
                }
            }
            return current || '';
        }
        return '';
    };

    // Helper function to check if a field is conditionally required
    const isFieldConditionallyRequired = (field) => {
        if (!field.conditional_requirements) {
            return false;
        }

        const condition = field.conditional_requirements.required_when;
        if (!condition) {
            return false;
        }

        const triggerField = condition.field;
        const triggerValue = getFieldValue(formData, triggerField);
        const operator = condition.operator;
        const expectedValue = condition.value;

        switch (operator) {
            case 'equals':
                return valuesEqualForConditionalRequirement(triggerValue, expectedValue);
            case 'contains':
                return (
                    Array.isArray(triggerValue) &&
                    triggerValue.some((tv) => valuesEqualForConditionalRequirement(tv, expectedValue))
                );
            case 'in':
                return (
                    Array.isArray(expectedValue) &&
                    expectedValue.some((ev) => valuesEqualForConditionalRequirement(triggerValue, ev))
                );
            case 'not_equals':
                return !valuesEqualForConditionalRequirement(triggerValue, expectedValue);
            default:
                return false;
        }
    };

    // Get effective requirement status (static or conditional)
    const getEffectiveRequiredStatus = (field) => {
        if (field.conditional_requirements) {
            return isFieldConditionallyRequired(field);
        }
        return field.is_required;
    };

    // Check if required additional fields are filled
    const checkRequiredAdditionalFields = () => {
        const requiredFields = additionalFields.filter(field => {
            // Only validate fields included in the current template (if one is set)
            if (templateFieldIds !== null && !templateFieldIds.includes(field.id)) return false;
            const isRequired = getEffectiveRequiredStatus(field);
            return isRequired && 
                field.field_key !== 'legal.declaration_date' && // Exclude auto-filled declaration date
                !field.field_key.includes('signature'); // Exclude all signature fields
        });
        
        for (const field of requiredFields) {
            const fieldValue = getFieldValue(formData, field.field_key);
            const stagedFiles = selectedFiles[field.field_key];
            
            // A field is valid if it has a value in DB/formData OR it has staged files ready to be uploaded
            const hasValue = (fieldValue && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0));
            const hasStagedFiles = (stagedFiles && stagedFiles.length > 0);
            
            if (!hasValue && !hasStagedFiles) {
                return false;
            }
        }
        return true;
    };

    // In the Registration component, keep the validationErrors state
    const [validationErrors, setValidationErrors] = useState({
        email: false,
        mobile: false,
        emailSuggestion: false,
        invalidDomain: false,
        dob: false,
        nric: false,
        sponsor_uen: false,
        isSME: false,
        isTeamRegistration: false,
    });

    /** Aligns with Step2 submit validation (incl. LX Copilot directory intake). */
    const isRegistrationFormReadyForSubmit = () => {
        const search = new URLSearchParams(location.search);
        const fullCert = search.get("full_certification");
        if (fullCert) {
            if (selectedCourseRunIds.length === 0) return false;
        } else if (!formData.course_run_id) {
            return false;
        }
        // Helper: is this system field present in the active template?
        const fieldInTemplate = (fieldKey) => {
            if (templateFieldIds === null) return true;
            const config = basicFieldsOrder.find(f => f.field_key === fieldKey);
            return !config || templateFieldIds.includes(config.id);
        };
        if (fieldInTemplate('nationality') && !formData.nationality) return false;
        if (fieldInTemplate('dob') && !formData.birth_age) return false;
        if (fieldInTemplate('participant_name') && !formData.participant_name) return false;
        if (fieldInTemplate('participant_email') && !formData.participant_email) return false;
        if (fieldInTemplate('mobile_no') && !formData.mobile_no) return false;
        if (fieldInTemplate('participant_email') && validationErrors.email) return false;
        if (fieldInTemplate('mobile_no') && validationErrors.mobile) return false;
        if (fieldInTemplate('dob') && validationErrors.dob) return false;
        if (formData.registration_type === "Company-Sponsored") {
            if (validationErrors.sponsor_uen || !formData.sponsor_uen) return false;
            if (validationErrors.isSME) return false;
            if (validationErrors.isTeamRegistration) return false;
        }
        if (
            fieldInTemplate('nric') &&
            (formData.nationality === "Singapore Citizen" ||
                formData.nationality === "Singapore Permanent Resident") &&
            (validationErrors.nric || !formData.nric)
        ) {
            return false;
        }
        const additionalFieldErrors = Object.keys(validationErrors).filter(
            (key) =>
                key.includes(".") &&
                validationErrors[key] !== false &&
                !key.includes("signature"),
        );
        if (additionalFieldErrors.length > 0) return false;
        return checkRequiredAdditionalFields();
    };

    useEffect(() => {
        const steps = [
            ['course_run_id', 'registration_type'],
            ['course_run_id', 'registration_type', 'participant_name', 'participant_email'],
            ['course_run_id', 'registration_type', 'participant_name', 'participant_email']
        ];

        let allowedTab = 0;

        for (let i = 0; i < steps.length; i++) {
            if (steps[i].every(field => formData[field])) {
                allowedTab = i + 1;
            } else {
                break;
            }
        }
        setAllowedTab(allowedTab);
    }, [formData])
    //Get supabase data
    const getAllCourseRuns = async () => {
        setLoadingCourseRuns(true);
        const today = new Date().toLocaleDateString('fr-CA'); // YYYY-MM-DD

        try {
            // Get all course runs with course_type details (start_date >= today)
            const { data, error } = await supabase
                .from("course_run")
                .select(`
                    *,
                    course_type (
                        code,
                        name,
                        certification,
                        price,
                        school,
                        ssg_course,
                        course_duration,
                        registration_template_id,
                        minimum_age
                    )
                `)
                .gte("start_date", today)
                .order("start_date");

            if (error) {
                console.error("Error fetching course runs:", { message: error.message, code: error.code, details: error.details });
                setCourseRuns([]);
                return;
            }

            const rows = data || [];
            console.log("Course runs from Supabase:", { count: rows.length, today, sample: rows[0] });

            if (rows.length === 0) {
                setCourseRuns([]);
                return;
            }

            // Fetch all sessions for these course runs
            const courseRunIds = rows.map(cr => cr.id);
            const { data: allSessions, error: sessionsError } = await supabase
                .from("session")
                .select("*")
                .in("course_run_code", courseRunIds)
                .order("session_date", { ascending: true });

            if (sessionsError) {
                console.error("Error fetching sessions:", sessionsError);
            }

            // Group sessions by course_run_code
            const sessionsByCourseRun = {};
            (allSessions || []).forEach(session => {
                if (!sessionsByCourseRun[session.course_run_code]) {
                    sessionsByCourseRun[session.course_run_code] = [];
                }
                sessionsByCourseRun[session.course_run_code].push(session);
            });

            // Transform data to match expected structure
            const transformedData = rows.map(course => {
                const sessions = sessionsByCourseRun[course.id] || [];
                const course_dates = sessions.map(session => ({
                    date: session.session_date,
                    start_time: session.start_time ? moment(session.start_time, "HH:mm:ss").format("HHmm") : null,
                    end_time: session.end_time ? moment(session.end_time, "HH:mm:ss").format("HHmm") : null,
                    room: session.custom_room || session.room_id,
                    venue: session.custom_venue || session.venue_id
                }));

                return {
                    ...course,
                    course_type_code: course.course_type_code,
                    name: course.course_type?.name || 'Unknown Course',
                    is_full_certification: false,
                    full_certification_id: null,
                    certification: course.course_type?.certification || null,
                    ssg_course: course.course_type?.ssg_course || false,
                    course_dates: course_dates
                };
            });

            setCourseRuns(transformedData);
            console.log("Fetched course run data: total =", transformedData.length);
        } catch (err) {
            console.error("getAllCourseRuns unexpected error:", err);
            setCourseRuns([]);
        } finally {
            setLoadingCourseRuns(false);
        }
    };


    useEffect(() => {
        getAllCourseRuns();
    }, []);

    useEffect(() => {
        document.title = "Registration";

        // Cleanup function to reset the title when component unmounts
        return () => {
            document.title = "Edthos Learning";
        };
    }, []);

    const calculatePrice = (course) => {
        // Base price calculation
        // const basePrice = course.name === "Python and GPT4 (ChatGPT) For Beginners" ? 1050 : course.price;
        let basePrice = course?.price;

        // Apply early bird discount if available and not expired
        let discountAmount = 0;
        if (course?.early_bird_discount && course?.early_bird_end_date) {
            const today = moment().format("YYYY-MM-DD");
            if (today <= course.early_bird_end_date) {
                discountAmount = course.early_bird_discount;
                basePrice = basePrice - discountAmount;
            }
        }

        // If not an SSG course, return discounted base price
        if (!course?.ssg_course) {
            return basePrice;
        }

        // Company-Sponsored cases
        if (formData.registration_type === "Company-Sponsored") {
            // SME case
            if (isSME === "yes" &&
                (formData.nationality === "Singapore Citizen" || formData.nationality === "Singapore Permanent Resident")) {
                return 0.3 * basePrice; // 70% subsidy
            }

            // Non-SME case - apply same logic as self-sponsored
            if (isSME === "no") {
                if (formData.nationality === "Singapore Citizen") {
                    return parseInt(formData.birth_age) >= 40 ? 0.3 * basePrice : 0.5 * basePrice;
                }
                if (formData.nationality === "Singapore Permanent Resident") {
                    return 0.5 * basePrice;
                }
            }
        }

        // Self-Sponsored cases
        if (formData.registration_type === "Self-Sponsored") {
            if (!formData.birth_age || !formData.nationality) {
                return basePrice;
            }

            if (formData.nationality === "Singapore Citizen") {
                return parseInt(formData.birth_age) >= 40 ? 0.3 * basePrice : 0.5 * basePrice;
            }
            if (formData.nationality === "Singapore Permanent Resident") {
                return 0.5 * basePrice;
            }
        }

        return basePrice;
    };

    // Calculate compulsory fields completion for the progress bar
    const getCompulsoryFieldsProgress = () => {
        const fields = new Map();

        fields.set('course', !!formData.course_run_id);

        const basicKeys = ['nationality', 'participant_name', 'participant_email', 'mobile_no', 'birth_age'];
        basicKeys.forEach(key => {
            const val = formData[key];
            fields.set(key, !!(val && val.toString().trim() !== ''));
        });

        // NRIC only required for SG citizens/PRs
        if (formData.nationality === 'Singapore Citizen' || formData.nationality === 'Singapore Permanent Resident') {
            fields.set('nric', !!(formData.nric && !validationErrors.nric));
        }

        if (formData.registration_type === "Company-Sponsored") {
            fields.set('sponsor_uen', !!(formData.sponsor_uen && !validationErrors.sponsor_uen));
            fields.set('is_sme', isSME === "yes" || isSME === "no");
            fields.set('team_reg', isTeamRegistration === "yes" || isTeamRegistration === "no");
        }

        if (additionalFields && additionalFields.length > 0) {
            additionalFields.forEach(field => {
                if (basicKeys.includes(field.field_key)) return;
                if (templateFieldIds !== null && field.id != null && !templateFieldIds.includes(field.id)) return;
                if (field.field_key === 'legal.declaration_date' || field.field_key.includes('signature') || field.field_key === 'terms') return;
                const isRequired = getEffectiveRequiredStatus(field);
                if (!isRequired) return;
                const val = formData[field.field_key] ?? (field.field_key.includes('.') ? field.field_key.split('.').reduce((o, k) => o?.[k], formData) : undefined);
                const stagedFiles = selectedFiles[field.field_key];
                fields.set(field.field_key, !!(
                    (val && val !== '' && (!Array.isArray(val) || val.length > 0)) ||
                    (stagedFiles && stagedFiles.length > 0)
                ));
            });
        }

        const filledCount = Array.from(fields.values()).filter(v => v).length;
        const totalCount = fields.size;
        return { filledCount, totalCount };
    };

    const progressData = getCompulsoryFieldsProgress();

    return (
        <div>
            {directoryIntakeSubmitted && (
                <Center minH="70vh" py={12} px={4} bg="white">
                    <VStack spacing={4} maxW="lg" textAlign="center">
                        <CheckCircleIcon color="green.500" boxSize={12} />
                        <Heading size="md">Application received</Heading>
                        <Text fontSize="sm" color="gray.600">
                            Your details have been sent. A recruitment agent from LX Copilot or your training
                            provider will follow up if anything else is needed.
                        </Text>
                    </VStack>
                </Center>
            )}
            {!directoryIntakeSubmitted && !loadingCourseRuns && !existingRegistration && formData.course_run_id && (
                <FloatingProgressBar
                    filledCount={progressData.filledCount}
                    totalCount={progressData.totalCount}
                    emailVerified={emailVerified}
                    emailVerificationSent={emailVerificationSent}
                    participantEmail={formData.participant_email}
                    onResendVerification={handleSendVerificationEmail}
                    isSendingVerification={isSendingVerification}
                    showEmailVerification={
                        registrationId && needsEmailVerification && !lxCopilotDirectoryIntake
                    }
                />
            )}
            {
                directoryIntakeSubmitted
                    ? null
                    : loadingCourseRuns
                    ?
                    <Center h="60vh">
                        <Spinner size="xl" thickness="4px" speed="0.65s" color="brand.600"/>
                    </Center>
                    :
                    <Stack
                        spacing="5"
                        width="100%"
                        bg={"white"}
                        px={{base: 4, md: 10}}
                        py={{base: 6, md: 10}}
                        borderRadius={{base: 0, md: "lg"}}
                        boxShadow="md"
                        mx={0}
                    >
                        {/* Rejection reason banner — shown when amending a rejected registration */}
                        {existingRegistration?.rejection_reason && (
                            <Alert status="error" borderRadius="md" mb={2}>
                                <AlertIcon />
                                <Box>
                                    <Text fontWeight="semibold" fontSize="sm">
                                        Your registration requires amendment
                                    </Text>
                                    <Text fontSize="sm">{existingRegistration.rejection_reason}</Text>
                                    <Text fontSize="xs" color="red.600" mt={1}>
                                        Please review your information below, make any necessary corrections, and resubmit.
                                    </Text>
                                </Box>
                            </Alert>
                        )}
                        {lxCopilotDirectoryIntake && directoryListingStatus === "checking" && (
                            <Alert status="info" borderRadius="md" mb={2}>
                                <AlertIcon />
                                <HStack>
                                    <Spinner size="sm" />
                                    <Text fontSize="sm">Verifying this course link against the directory…</Text>
                                </HStack>
                            </Alert>
                        )}
                        {lxCopilotDirectoryIntake && directoryListingStatus === "invalid" && (
                            <Alert status="error" borderRadius="md" mb={2}>
                                <AlertIcon />
                                <Text fontSize="sm">
                                    This course is not available from the directory, or the link is incorrect or
                                    expired. Please use the link from the course directory or contact support.
                                </Text>
                            </Alert>
                        )}
                        {lxCopilotDirectoryIntake && directoryListingStatus === "valid" && (
                            <Alert status="info" borderRadius="md" mb={2} bg="orange.50" borderColor="orange.200">
                                <AlertIcon color="orange.500" />
                                <Box>
                                    <Text fontWeight="semibold" fontSize="sm">
                                        LX Copilot
                                    </Text>
                                    <Text fontSize="sm">
                                        You will be served by a recruitment agent from LX Copilot for this application.
                                    </Text>
                                </Box>
                            </Alert>
                        )}
                        <HStack justify="space-between" align="center" mb={10}>
                            <Heading size={"sm"} borderBottom="2px solid"
                                     borderColor={"brand.400"} pb={2} width="fit-content">
                                Course Registration
                            </Heading>
                            {(new URLSearchParams(location.search).get('certificate') || 
                              new URLSearchParams(location.search).get('full_certification') || 
                              new URLSearchParams(location.search).get('course')) && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/courses-and-programs')}
                                    leftIcon={<Icon as={HiArrowLeft} />}
                                >
                                    Back to Course Selection
                                </Button>
                            )}
                        </HStack>
                        <Step1
                            courseRuns={courseRuns}
                            formData={formData}
                            setFormData={setFormData}
                            allowedTab={allowedTab}
                            setTabIndex={setTabIndex}
                            validationErrors={validationErrors}
                            setValidationErrors={setValidationErrors}
                            isTeamRegistration={isTeamRegistration}
                            setIsTeamRegistration={setIsTeamRegistration}
                            calculatePrice={calculatePrice}
                            registrationId={registrationId}
                            setRegistrationId={setRegistrationId}
                            isSME={isSME}
                            setIsSME={setIsSME}
                            isUENValid={isUENValid}
                            setIsUENValid={setIsUENValid}
                            selectedCourseRunIds={selectedCourseRunIds}
                            setSelectedCourseRunIds={setSelectedCourseRunIds}
                            fullCertificationCost={fullCertificationCost}
                            additionalFields={additionalFields}
                            templateFieldIds={templateFieldIds}
                            orgGstSettings={orgGstSettings}
                            selectedFiles={selectedFiles}
                            setSelectedFiles={setSelectedFiles}
                            uploadingFiles={uploadingFiles}
                            setUploadingFiles={setUploadingFiles}
                            existingRegistration={existingRegistration}
                            setEmailVerified={setEmailVerified}
                            basicFieldsOrder={basicFieldsOrder}
                        />
                        <Step2
                            courseRuns={courseRuns}
                            formData={formData}
                            setFormData={setFormData}
                            allowedTab={allowedTab}
                            setTabIndex={setTabIndex}
                            validationErrors={validationErrors}
                            isTeamRegistration={isTeamRegistration}
                            calculatePrice={calculatePrice}
                            isSME={isSME}
                            setIsSME={setIsSME}
                            isUENValid={isUENValid}
                            setIsUENValid={setIsUENValid}
                            registrationId={registrationId}
                            setRegistrationId={setRegistrationId}
                            checkRequiredAdditionalFields={checkRequiredAdditionalFields}
                            getFieldValue={getFieldValue}
                            additionalFields={additionalFields}
                            toast={toast}
                            selectedCourseRunIds={selectedCourseRunIds}
                            setSelectedCourseRunIds={setSelectedCourseRunIds}
                            selectedFiles={selectedFiles}
                            uploadingFiles={uploadingFiles}
                            emailVerified={emailVerified}
                            sendVerificationEmail={sendVerificationEmail}
                            existingRegistration={existingRegistration}
                            needsEmailVerification={needsEmailVerification}
                            emailVerificationSent={emailVerificationSent}
                            isSendingVerification={isSendingVerification}
                            onSendVerificationEmail={handleSendVerificationEmail}
                            gateOnRunConfirmed={gateOnRunConfirmed}
                            orgGstSettings={orgGstSettings}
                            lxCopilotDirectoryIntake={lxCopilotDirectoryIntake}
                            directoryListingStatus={directoryListingStatus}
                            directoryChannelParam={directoryChannelParam}
                            templateFieldIds={templateFieldIds}
                            onDirectoryIntakeSuccess={() => setDirectoryIntakeSubmitted(true)}
                            lxDirectoryFormReady={isRegistrationFormReadyForSubmit()}
                        />
                    </Stack>
            }
        </div>
    );
};
